import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Water } from 'three/examples/jsm/objects/Water.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import Stats from 'three/examples/jsm/libs/stats.module.js';

// Post-processing imports for Wind Waker style
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { applyCelShading, createCelMesh } from './shaders/CelShader.js';
import { FISH_TYPES, initializeFishModels, getRandomFish, createFishSplash } from './Fish.js';
import { createHouse, createHouseInterior, createIrisWipeEffect, transitionToInterior, transitionToExterior } from './House.js';
import { createTerrain, getTerrainHeight } from './Terrain.js';

// Import core modules
import { eventBus, GameEvents } from './core/index.js';
import { gameState as coreGameState } from './core/GameState.js';
import {
  FIXED_DELTA_TIME,
  GROUND_RADIUS,
  PLAYER_BASE_SPEED,
  MINIGAME_DIFFICULTY,
  COLORS
} from './core/Constants.js';

// Import game systems
import { achievementSystem } from './systems/AchievementSystem.js';
import { progressionSystem } from './systems/ProgressionSystem.js';
import { tutorialSystem } from './systems/TutorialSystem.js';

// Import UI modules
import { initPanels, updateProgressionHUD, isAnyPanelOpen } from './ui/index.js';
import { initFishPreview, showFishPreview, closeFishPreview } from './ui/FishPreview.js';

// Initialize clock for deltaTime at the very start of the file
const clock = new THREE.Clock();

// Create FPS counter at the start of the file
// This ensures it's available throughout the code
const stats = new Stats();
stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom

// Initialize empty arrays for key game objects that are used by the animation loop
const fishingHoles = [];

// Pre-declare key variables needed by functions
let groundRadius = 55; // Default radius for the circular ground (extended for sand overlap)
let rockTexture = null; // Will be populated when textures are loaded
let ground = null; // Ground mesh - will be created in initScene
let player = null; // Player mesh - will be created in initScene

// Fishing hole positions - needed early for terrain generation
const fishingHolePositions = [
  { x: -8, z: -5 },
  { x: 5, z: 3 },
  { x: -3, z: 8 },
  { x: 10, z: -8 },
  { x: -20, z: -15 },
  { x: 18, z: 12 },
  { x: -15, z: 22 },
  { x: 25, z: -20 }
];

// Pre-declare texture variables for async loading
let splatMapTexture = null;
let grassTexture = null;
let sandTexture = null;
let dirtTexture = null;
let grassHeightMap = null;
let sandHeightMap = null;
let dirtHeightMap = null;
let rockHeightMap = null;

// Pre-declare minigame variable
let horizontalMinigame = null;

// Pre-declare beach mesh for raycasting
// sandBeach is now integrated into terrain

// Pre-declare the createStylizedRockBorder function so it can be called anywhere
function createStylizedRockBorder() {
  console.log('Creating 3D rock border...');
  
  // First remove any existing rock borders to avoid duplicates
  scene.children.forEach(child => {
    if (child.userData && child.userData.isRockBorder) {
      scene.remove(child);
    }
  });
  
  // Border parameters with 1/8 the height, precisely positioned at the ground edge
  const torusRadius = groundRadius - 0.05; // Positioned right at the ground edge
  const tubeRadius = 0.15; // One eighth as tall (was 1.2, then 0.6, then 0.3)
  const radialSegments = 12; // More segments for smoother look
  const tubularSegments = 72; // Decent number of segments around the circle
  
  // Create a torus for our 3D border
  const borderGeometry = new THREE.TorusGeometry(
    torusRadius,
    tubeRadius,
    radialSegments,
    tubularSegments
  );
  
  // Very simple UV modification to make rocks visible
  // Get the UV attribute
  const uvs = borderGeometry.attributes.uv;
  
  // Custom mapping to fix horizontal stretching - need much more balanced aspect ratio
  for (let i = 0; i < uvs.count; i++) {
    // Get original UVs
    const origU = uvs.getX(i);
    const origV = uvs.getY(i);
    
    // EXTREME texture repetition to eliminate horizontal stretching
    // For a torus, we need much higher repetition in the circular direction
    const u = origU * 96; // Double again to 96 repeats around the circle
    
    // Slightly more vertical repeats to balance
    const v = origV * 6;
    
    uvs.setXY(i, u, v);
  }
  
  // Mark UVs for update
  borderGeometry.attributes.uv.needsUpdate = true;
  console.log('Simple UV scaling applied to border - should show rock pattern');
  
  // Create a bright, visible material inspired by the semi-transparent sand borders
  let borderMaterial = new THREE.MeshStandardMaterial({
    color: 0xdfd0c0, // Very light tan color for maximum visibility
    emissive: 0x221100, // Very subtle self-illumination
    roughness: 0.7,
    metalness: 0.2, // Slight metalness for highlights
    side: THREE.DoubleSide,
    bumpScale: 0.4, // Stronger bump to make texture visible
    envMapIntensity: 1.2 // Stronger environmental response
  });
  
  // Use grass texture for the border
  if (grassTexture) {
    // Use the existing loaded grass texture directly
    borderMaterial.map = grassTexture;
    borderMaterial.needsUpdate = true;
    console.log('Applied existing grass texture to border');
  } else {
    // Load a new grass texture if needed
    const borderTextureLoader = new THREE.TextureLoader();
    borderTextureLoader.load('/textures/albedo-grass.png', function(texture) {
      console.log('Loaded direct grass texture for border');
      texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      borderMaterial.map = texture;
      borderMaterial.needsUpdate = true;
    });
  }
  
  // Use grass height map
  if (grassHeightMap) {
    // Use existing grass height map
    borderMaterial.bumpMap = grassHeightMap;
    borderMaterial.needsUpdate = true;
  } else {
    // Load grass height map if needed
    const heightLoader = new THREE.TextureLoader();
    heightLoader.load('/textures/grassheight.png', function(texture) {
      console.log('Loaded grass height map for border');
      texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      borderMaterial.bumpMap = texture;
      borderMaterial.needsUpdate = true;
    });
  }
  
  // Note: No need to set color here as it's already set in the texture load callback
  // The initial color will be used until the texture loads
  
  // Create the border mesh with our 3D torus geometry
  const borderMesh = new THREE.Mesh(borderGeometry, borderMaterial);
  // Rotate the torus to lie flat around the ground
  borderMesh.rotation.x = Math.PI / 2;
  // Position it precisely to align with the ground edge
  borderMesh.position.y = 0.15; // Very low position for perfect edge alignment
  // Scale it precisely to cover the edge without gaps
  borderMesh.scale.set(1.0, 1.0, 1.0); // Exact scale to match ground edge
  borderMesh.receiveShadow = true;
  borderMesh.castShadow = true; // Important for 3D effect
  borderMesh.userData.isRockBorder = true;
  scene.add(borderMesh);
  console.log('Rock border added to scene');
  
  return borderMesh;
}

// Game state
const gameState = {
  fishCaught: 0,
  isFishing: false,
  activeFishingHole: null,
  fishingRod: null,
  fishingLine: null,
  fishingFloat: null,
  fishBiting: false,
  fishingTimer: null,
  caughtFish: null,
  score: 0,
  playerMoving: false,
  playerTargetPosition: null,
  playerMovementSpeed: 0.12,
  inventory: {}, // Track caught fish by name
  showInventory: false, // Flag to toggle inventory display
  fishData: {}, // Added fishData property
  isInHouse: false, // Track if player is inside the house
  house: null, // Reference to the house object
  houseInterior: null, // Reference to the house interior
  irisEffect: null, // Reference to the iris wipe effect
  isTransitioning: false, // Flag to prevent multiple transitions
  weather: 'cloudy', // Initial weather state: 'cloudy' by default
  pendingOceanFishing: null // Pending ocean fishing hole when walking to beach
};

// Game loading state tracking
let welcomeAssetsLoaded = false;
let gameAssetsManager = new THREE.LoadingManager();
let loadingComplete = false;
let gameStarted = false;

// First, load all welcome screen assets synchronously
function loadWelcomeScreenAssets() {
  const welcomeAssets = [
    '/textures/fishfishfish.png'
  ];
  
  let assetsLoaded = 0;
  const totalAssets = welcomeAssets.length;
  
  // Update loading text to show we're loading welcome screen assets
  const loadingText = document.getElementById('loading-text');
  if (loadingText) {
    loadingText.textContent = 'Loading welcome screen...';
  }
  
  // Create a simple loader for welcome screen assets
  const imageLoader = new THREE.ImageLoader();
  
  welcomeAssets.forEach(asset => {
    imageLoader.load(asset, () => {
      assetsLoaded++;
      if (assetsLoaded === totalAssets) {
        welcomeAssetsLoaded = true;
        // Now that welcome screen assets are loaded, start loading game assets
        startLoadingGameAssets();
      }
    });
  });
}

// Start loading game assets after welcome screen assets are loaded
function startLoadingGameAssets() {
  // Set up loading manager for game assets
  // Track loading progress
  gameAssetsManager.onProgress = function(url, itemsLoaded, itemsTotal) {
    const progressPercent = (itemsLoaded / itemsTotal) * 100;
    const loadingBar = document.getElementById('loading-bar');
    const loadingText = document.getElementById('loading-text');
    
    if (loadingBar) {
      loadingBar.style.width = progressPercent + '%';
    }
    
    if (loadingText) {
      loadingText.textContent = `Loading game assets... ${Math.floor(progressPercent)}%`;
    }
  };

  // When all game assets are loaded
  gameAssetsManager.onLoad = function() {
    loadingComplete = true;
    const loadingText = document.getElementById('loading-text');
    const startButton = document.getElementById('start-button');
    const fishImage = document.getElementById('dumbfish-image');
    
    if (loadingText) {
      loadingText.textContent = 'All assets loaded!';
    }
    
    if (startButton) {
      startButton.style.display = 'block';
      startButton.addEventListener('click', startGame);
    }
    
    // Make the fish image clickable too
    if (fishImage) {
      fishImage.style.cursor = 'pointer';
      fishImage.addEventListener('click', startGame);
      // Add a subtle hover effect
      fishImage.addEventListener('mouseenter', () => {
        fishImage.style.transform = 'scale(1.1)';
      });
      fishImage.addEventListener('mouseleave', () => {
        fishImage.style.transform = '';
      });
    }
  };
  
  // Now initialize the scene which will use gameAssetsManager for loading
  initScene();
}

// Start loading welcome screen assets immediately
loadWelcomeScreenAssets();

// Start game function
function startGame(event) {
  // Prevent the click from being registered as a game interaction
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Get player name from input
  const nameInput = document.getElementById('player-name-input');
  const playerName = nameInput && nameInput.value.trim() ? nameInput.value.trim() : 'Player';
  window.playerName = playerName; // Store globally for multiplayer

  // Play the splash intro sound
  const splashSound = document.getElementById('splash-intro');
  if (splashSound) {
    splashSound.currentTime = 0;
    splashSound.play().catch(error => console.log("Error playing splash sound: ", error));
  }

  const welcomeScreen = document.getElementById('welcome-screen');
  if (welcomeScreen) {
    welcomeScreen.style.display = 'none';
  }
  gameStarted = true;

  // Connect to multiplayer after name is set
  console.log(`[Game] Starting with player name: ${window.playerName}`);
  setTimeout(() => connectMultiplayer(), 500);

  // Initialize inventory and event listeners again to ensure they're properly set up
  initializeInventory();
  setupInventoryListeners();
  
  // Make sure inventory button works
  const inventoryButton = document.getElementById('inventory-button');
  if (inventoryButton) {
    // Remove any existing listeners to avoid duplicates
    const newInventoryButton = inventoryButton.cloneNode(true);
    inventoryButton.parentNode.replaceChild(newInventoryButton, inventoryButton);
    newInventoryButton.addEventListener('click', toggleInventory);
  }

  // Initialize UI panels (achievements, settings, progression HUD)
  initPanels();

  // Initialize fish preview modal
  initFishPreview();

  // Initialize pond tooltip listeners
  setupPondTooltipListeners();

  // Add keyboard event listener for 'I' key
  window.addEventListener('keydown', onKeyDown);
  
  // Start the animation loop
  animate();
  
  // Start background music if available
  const backgroundMusic = document.getElementById('background-music');
  if (backgroundMusic) {
    backgroundMusic.play().catch(error => console.log("Autoplay prevented: ", error));
  }

  // Start tutorial for new players (after a short delay)
  setTimeout(() => {
    if (!tutorialSystem.completed) {
      tutorialSystem.start();
    }
  }, 1000);

  // Emit game started event
  eventBus.emit(GameEvents.GAME_STARTED);
}

// Scene setup
const scene = new THREE.Scene();

// Game configuration flags
const gameConfig = {
  showGroundLeaves: true,  // Toggle to show/hide fallen leaves around trees
};

// --- Cloud and Shadow Animation Speeds ---
const SHADOW_TEXTURE_UV_SPEED_X = 0.5; // Speed for ground shadow texture UV scroll in X
const SHADOW_TEXTURE_UV_SPEED_Z = 0.3; // Speed for ground shadow texture UV scroll in Z (texture's Y-axis)
const GROUND_SHADOW_TIME_MULTIPLIER = 0.05; // Overall multiplier for ground shadow animation time

// How far clouds move in world space relative to a complete UV texture cycle (0 to 1)
// Higher values mean the clouds move farther for each complete texture cycle
const CLOUD_WORLD_MOVEMENT_SCALE = 40;

let skyClouds = []; // Array to hold our cloud sprites
const cloudAreaSize = 120; // Area over which clouds will roam (a bit larger than ground)
const cloudHeight = 25;    // How high the clouds are
const numClouds = 15;       // Number of clouds

// Cloud shadow texture
let cloudShadowTexture = null;

// Load cloud shadow texture directly
const cloudTextureLoader = new THREE.TextureLoader();
try {
  cloudShadowTexture = cloudTextureLoader.load('/textures/cloud_shadow.png', (texture) => {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    console.log('Cloud shadow texture loaded successfully.');
    if (ground && ground.material && ground.material.uniforms.cloudShadowTexture) {
      ground.material.uniforms.cloudShadowTexture.value = texture;
      ground.material.needsUpdate = true;
    }
  }, undefined, (err) => {
    console.warn('Cloud shadow texture not found or failed to load. Using fallback. Place cloud_shadow.png in /textures/ for custom shadows.', err);
    // Shader will use its internal fallback 1x1 white texture
  });
} catch (error) {
  console.warn('Error during texture loader instantiation or load call for cloud_shadow.png:', error);
}

// --- Sky Clouds Setup ---
function createSkyClouds() {
  const textureLoader = new THREE.TextureLoader(gameAssetsManager);
  
  // Array to hold the different cloud textures
  const cloudTextures = [];
  
  // Load the three different cloud textures
  try {
    // Load cloud1.png
    textureLoader.load('/textures/cloud1.png', (texture1) => {
      console.log('Cloud 1 texture loaded successfully');
      cloudTextures.push(texture1);
      createCloudsIfAllTexturesLoaded();
    });
    
    // Load cloud2.png
    textureLoader.load('/textures/cloud2.png', (texture2) => {
      console.log('Cloud 2 texture loaded successfully');
      cloudTextures.push(texture2);
      createCloudsIfAllTexturesLoaded();
    });
    
    // Load cloud3.png
    textureLoader.load('/textures/cloud3.png', (texture3) => {
      console.log('Cloud 3 texture loaded successfully');
      cloudTextures.push(texture3);
      createCloudsIfAllTexturesLoaded();
    });
    
    // Function to create clouds once all textures are loaded
    function createCloudsIfAllTexturesLoaded() {
      // Only proceed if all 3 textures are loaded
      if (cloudTextures.length >= 3) {
        console.log('All cloud textures loaded, creating clouds...');
        
        // Create each cloud with a random texture from our collection
        for (let i = 0; i < numClouds; i++) {
          // Randomly select one of the three cloud textures
          const randomTextureIndex = Math.floor(Math.random() * 3);
          const selectedTexture = cloudTextures[randomTextureIndex];
          
          // Create cloud material with the randomly selected texture
          const cloudMaterial = new THREE.SpriteMaterial({
            map: selectedTexture,
            color: 0xffffff,
            transparent: true,
            alphaTest: 0.1, // Ensures crisp edges
          });
          
          // Create cloud sprite with this material
          const cloudSprite = new THREE.Sprite(cloudMaterial);
          
          // Random initial position
          cloudSprite.position.set(
            (Math.random() - 0.5) * cloudAreaSize,
            cloudHeight + (Math.random() - 0.5) * 10, // Slight height variation
            (Math.random() - 0.5) * cloudAreaSize
          );
          
          // Random scale - slightly different for each cloud type
          const baseScale = 10 + Math.random() * 8;
          const widthRatio = 0.6 + (randomTextureIndex * 0.1); // Different width/height ratio per cloud type
          cloudSprite.scale.set(baseScale, baseScale * widthRatio, 1.0);
          
          // Set initial visibility based on weather
          cloudSprite.visible = (gameState.weather === 'cloudy');
          
          // Add to scene and tracking array
          scene.add(cloudSprite);
          skyClouds.push(cloudSprite);
        }
      }
    }
  } catch (error) {
    console.warn('Error loading cloud textures:', error);
  }
}

// Camera setup - Animal Crossing style isometric view
const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 1000);

// Isometric camera settings
const cameraSettings = {
  distance: 25,        // Distance from player
  height: 20,          // Height above player
  angle: 0,            // Rotation angle around player (0 = looking from south)
  smoothing: 0.08,     // Camera follow smoothness (lower = smoother)
  interiorDistance: 18,
  interiorHeight: 15
};

// Initial camera position (will be updated to follow player)
camera.position.set(0, cameraSettings.height, cameraSettings.distance);

// Renderer setup
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Set pixel ratio (capped at 2 for performance)
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setClearAlpha(0); // Make the renderer background transparent
document.body.appendChild(renderer.domElement);

// Set renderer properties for better quality - Wind Waker style
// Shadow mapping disabled for performance - using blob shadows instead
renderer.shadowMap.enabled = false;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.2; // Slightly brighter

// ===========================================
// POST-PROCESSING - Wind Waker Style
// ===========================================

// Custom shader for color enhancement (saturation + vibrance)
const ColorEnhanceShader = {
  name: 'ColorEnhanceShader',
  uniforms: {
    tDiffuse: { value: null },
    saturation: { value: 1.25 },
    brightness: { value: 1.05 },
    contrast: { value: 1.08 },
    vibrance: { value: 0.15 }
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform float saturation;
    uniform float brightness;
    uniform float contrast;
    uniform float vibrance;
    varying vec2 vUv;

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);

      // Apply brightness
      color.rgb *= brightness;

      // Apply contrast
      color.rgb = (color.rgb - 0.5) * contrast + 0.5;

      // Calculate luminance
      float luma = dot(color.rgb, vec3(0.299, 0.587, 0.114));

      // Apply saturation
      color.rgb = mix(vec3(luma), color.rgb, saturation);

      // Apply vibrance
      float maxC = max(color.r, max(color.g, color.b));
      float minC = min(color.r, min(color.g, color.b));
      float sat = maxC - minC;
      color.rgb = mix(vec3(luma), color.rgb, 1.0 + vibrance * (1.0 - sat));

      // Warm tint
      color.rgb *= vec3(1.02, 1.0, 0.98);

      gl_FragColor = color;
    }
  `
};

// Declare composer variables (initialized later after scene is ready)
let composer = null;
let renderPass = null;
let bloomPass = null;
let colorEnhancePass = null;

// Function to initialize post-processing (called after scene setup)
function initPostProcessing() {
  console.log('initPostProcessing called');

  // Get the actual render size (accounting for pixel ratio)
  const width = window.innerWidth;
  const height = window.innerHeight;
  const pixelRatio = renderer.getPixelRatio();

  // Create render target with correct size
  const renderTarget = new THREE.WebGLRenderTarget(
    width * pixelRatio,
    height * pixelRatio,
    {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat
    }
  );

  composer = new EffectComposer(renderer, renderTarget);

  // RenderPass - renders the scene
  renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  // Bloom for that soft glow
  bloomPass = new UnrealBloomPass(
    new THREE.Vector2(width, height),
    0.3,    // Bloom strength (lower)
    0.4,    // Bloom radius
    0.85    // Bloom threshold (higher = less bloom)
  );
  composer.addPass(bloomPass);

  // Color enhancement pass
  colorEnhancePass = new ShaderPass(ColorEnhanceShader);
  composer.addPass(colorEnhancePass);

  console.log('Post-processing setup complete');
}

// Controls - Disabled for Animal Crossing style camera
// const controls = new OrbitControls(camera, renderer.domElement);
// controls.enableDamping = true;
// controls.dampingFactor = 0.05;
// controls.minDistance = 5;
// controls.maxDistance = 60;
// controls.maxPolarAngle = Math.PI / 2 - 0.1;
// controls.update();

/**
 * Update camera to follow player (Animal Crossing style)
 */
// Smooth camera look-ahead values (persist between frames)
let cameraLookAheadX = 0;
let cameraLookAheadZ = 0;

function updateCameraFollow() {
  if (!player) return;

  // Get current settings based on location
  const isInterior = gameState.isInHouse;
  const distance = isInterior ? cameraSettings.interiorDistance : cameraSettings.distance;
  const height = isInterior ? cameraSettings.interiorHeight : cameraSettings.height;

  // Zelda-style: camera leads slightly in movement direction
  let targetLookAheadX = 0;
  let targetLookAheadZ = 0;
  if (gameState.playerMoving && !isInterior) {
    const leadDistance = 2;
    targetLookAheadX = Math.sin(player.rotation.y) * leadDistance;
    targetLookAheadZ = Math.cos(player.rotation.y) * leadDistance;
  }

  // Smoothly interpolate look-ahead (prevents flash when stopping)
  const lookAheadSmoothing = 0.03;
  cameraLookAheadX += (targetLookAheadX - cameraLookAheadX) * lookAheadSmoothing;
  cameraLookAheadZ += (targetLookAheadZ - cameraLookAheadZ) * lookAheadSmoothing;

  // Calculate target camera position
  const targetX = player.position.x + cameraLookAheadX + Math.sin(cameraSettings.angle) * distance;
  const targetY = player.position.y + height;
  const targetZ = player.position.z + cameraLookAheadZ + Math.cos(cameraSettings.angle) * distance;

  // Smoothly interpolate camera position
  const smoothing = 0.05;
  camera.position.x += (targetX - camera.position.x) * smoothing;
  camera.position.y += (targetY - camera.position.y) * smoothing;
  camera.position.z += (targetZ - camera.position.z) * smoothing;

  // Camera looks at player (with smoothed look ahead)
  const lookAtX = player.position.x + cameraLookAheadX * 0.5;
  const lookAtY = player.position.y + 1;
  const lookAtZ = player.position.z + cameraLookAheadZ * 0.5;
  camera.lookAt(lookAtX, lookAtY, lookAtZ);
}

// Lighting
// Wind Waker style ambient - slight cool blue tint for shadows
const ambientLight = new THREE.AmbientLight(0xe8e8ff, 0.7); // Cool blue-ish white, softer
scene.add(ambientLight);

// Add a hemisphere light for more natural outdoor lighting (sky/ground colors)
const hemisphereLight = new THREE.HemisphereLight(
  0x87ceeb, // Sky color - light blue
  0x98d982, // Ground color - grass green
  0.4       // Intensity
);
scene.add(hemisphereLight);

// Wind Waker style sunlight - warm golden color
const directionalLight = new THREE.DirectionalLight(0xfffae6, 1.4); // Warm white, stronger
directionalLight.position.set(25, 40, 20); // Higher sun angle for dramatic shadows
directionalLight.castShadow = true;
directionalLight.shadow.mapSize.width = 2048;
directionalLight.shadow.mapSize.height = 2048;
directionalLight.shadow.camera.near = 0.5;
directionalLight.shadow.camera.far = 100;
directionalLight.shadow.camera.left = -50;
directionalLight.shadow.camera.right = 50;
directionalLight.shadow.camera.top = 50;
directionalLight.shadow.camera.bottom = -50;
directionalLight.shadow.bias = -0.0005; // Reduce shadow acne
directionalLight.shadow.normalBias = 0.02; // Helps with shadow artifacts on curved surfaces
scene.add(directionalLight);

// Create GLTFLoader with game assets manager
const gltfLoader = new GLTFLoader(gameAssetsManager);

// Initialize fish models with the proper loader
initializeFishModels(gltfLoader);

// Determine initial cloud shadow strength and offset based on weather
const initialCloudShadowStrength = gameState.weather === 'cloudy' ? 0.5 : 0.0; // Adjusted to 0.5
const initialCloudShadowOffsetX = gameState.weather === 'cloudy' ? Math.random() : 0.0;
const initialCloudShadowOffsetY = gameState.weather === 'cloudy' ? Math.random() : 0.0;

// Terrain geometry is now handled by the Terrain.js module

// Load splat map
let splatMapCanvas = null;

// Function to add natural sand patches around ponds
// Creating subtle sand border rings using meshes for perfect positioning
function createSandBorders() {
  // Remove any existing sand meshes first to avoid duplicates
  scene.children.forEach(child => {
    if (child.userData && child.userData.isSandBorder) {
      scene.remove(child);
    }
  });
  
  // For each pond, create just an inner accent ring
  fishingHolePositions.forEach((position, index) => {
    console.log(`Creating sand accent ring for pond at: (${position.x}, ${position.z})`);
    
    // Create a hand-drawn style irregular ring
    const innerRadius = 2.0; // Base pond radius
    const thickness = 0.25; // Ring thickness
    
    // Create custom shape with small irregularities to look hand-drawn
    // We'll create a custom shape with vertices that deviate slightly from a perfect circle
    const segments = 32;
    const angleStep = Math.PI * 2 / segments;
    
    // Create shape for outer edge
    const ringShape = new THREE.Shape();
    const seed = index; // Use pond index as random seed for variations
    
    // Generate outer points with irregularity
    for (let i = 0; i <= segments; i++) {
      const angle = i * angleStep;
      // Add small variations to radius (±10%)
      const radiusVariation = 0.1 * Math.sin(angle * 4 + seed) + 0.08 * Math.cos(angle * 7 + seed * 3);
      const outerRadius = (innerRadius + thickness) * (1 + radiusVariation * 0.1);
      
      const x = Math.cos(angle) * outerRadius;
      const y = Math.sin(angle) * outerRadius;
      
      if (i === 0) {
        ringShape.moveTo(x, y);
      } else {
        ringShape.lineTo(x, y);
      }
    }
    
    // Create hole for inner edge (also with irregularity)
    const holePath = new THREE.Path();
    let firstX, firstY;
    
    for (let i = 0; i <= segments; i++) {
      const angle = i * angleStep;
      // Add small variations to inner radius (±8%)
      const radiusVariation = 0.08 * Math.sin(angle * 5 + seed * 2) + 0.06 * Math.cos(angle * 8 + seed);
      const actualInnerRadius = innerRadius * (1 + radiusVariation * 0.08);
      
      const x = Math.cos(angle) * actualInnerRadius;
      const y = Math.sin(angle) * actualInnerRadius;
      
      if (i === 0) {
        holePath.moveTo(x, y);
        firstX = x;
        firstY = y;
      } else {
        holePath.lineTo(x, y);
      }
    }
    
    // Close the path
    holePath.lineTo(firstX, firstY);
    
    // Add the hole to the shape
    ringShape.holes.push(holePath);
    
    // Create geometry from the shape
    const accentGeometry = new THREE.ShapeGeometry(ringShape);
    
    // Create material
    const accentMaterial = new THREE.MeshBasicMaterial({
      color: 0xd9b162, // Warm sand color
      transparent: true,
      opacity: 0.5, // 50% opacity for subtle accent
      side: THREE.DoubleSide,
      depthWrite: false // Helps with transparency
    });
    
    const accentRing = new THREE.Mesh(accentGeometry, accentMaterial);
    accentRing.userData.isSandBorder = true;
    accentRing.position.set(position.x, 0.02, position.z); // Slightly above water level
    accentRing.rotation.x = -Math.PI / 2; // Rotate to lie flat
    accentRing.receiveShadow = true;
    scene.add(accentRing);
  });
  
  console.log('Subtle inner accent rings created for ponds');
}

// Sand is now implemented with direct 3D meshes, so this function just returns the texture unmodified
function addSandAroundPonds(texture) {
  // No longer modifying the texture - using direct 3D approach instead
  return texture;
}

// No placeholder function needed - all references have been removed

// Helper function to modify texture if needed
function modifySplatTexture(texture) {
  // Just return the original texture unmodified
  return texture;
}

// Splat map no longer needed - terrain uses height-based texture blending
function loadSplatMap() {
  // No-op - heightmap terrain handles texture blending automatically
  console.log('Splat map not needed for heightmap terrain.');
}

// Load terrain textures for heightmap terrain
function loadTerrainTextures() {
  const textureLoader = new THREE.TextureLoader(gameAssetsManager);
  try {
    // Load albedo textures - only grass, sand, dirt are used by heightmap terrain
    grassTexture = textureLoader.load('/textures/albedo-grass.png', (texture) => {
      texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      if (ground && ground.material && ground.material.uniforms.grassTexture) {
        ground.material.uniforms.grassTexture.value = texture;
        ground.material.needsUpdate = true;
      }
    }, undefined, (err) => {
      console.warn('Grass texture not found.', err);
    });

    sandTexture = textureLoader.load('/textures/albedo-sand.png', (texture) => {
      texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      if (ground && ground.material && ground.material.uniforms.sandTexture) {
        ground.material.uniforms.sandTexture.value = texture;
        ground.material.needsUpdate = true;
      }
    }, undefined, (err) => {
      console.warn('Sand texture not found.', err);
    });

    dirtTexture = textureLoader.load('/textures/albedo-dirt.png', (texture) => {
      texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      if (ground && ground.material && ground.material.uniforms.dirtTexture) {
        ground.material.uniforms.dirtTexture.value = texture;
        ground.material.needsUpdate = true;
      }
    }, undefined, (err) => {
      console.warn('Dirt texture not found.', err);
    });

    // Rock texture and height maps not used by heightmap terrain
    console.log('Terrain textures loading...');
  } catch (error) {
    console.warn('Error loading terrain textures:', error);
  }
}

// Create heightmap terrain with procedural height variation
ground = createTerrain({
  radius: groundRadius,
  segments: 128,
  maxHeight: 2.0,
  seed: 42,
  pondPositions: fishingHolePositions,
  pondRadius: 3,
  grassTexture: grassTexture,
  sandTexture: sandTexture,
  dirtTexture: dirtTexture,
  cloudShadowTexture: cloudShadowTexture
});

// Set initial cloud shadow values
if (ground.material.uniforms) {
  ground.material.uniforms.cloudShadowOffset.value.set(initialCloudShadowOffsetX, initialCloudShadowOffsetY);
  ground.material.uniforms.cloudShadowStrength.value = initialCloudShadowStrength;
}

ground.receiveShadow = true;
scene.add(ground);

// Helper function to get terrain height at a world position
const terrainRaycaster = new THREE.Raycaster();
function getTerrainHeightAt(x, z) {
  if (!ground) return 0;

  // Ensure world matrix is up to date for accurate raycasting
  ground.updateMatrixWorld(true);

  terrainRaycaster.set(
    new THREE.Vector3(x, 50, z), // Start high above
    new THREE.Vector3(0, -1, 0)  // Cast downward
  );
  const intersects = terrainRaycaster.intersectObject(ground);
  if (intersects.length > 0) {
    return intersects[0].point.y;
  }
  return 0; // Default to ground level
}

// === SCATTERED ROCKS AROUND ISLAND EDGE ===
// Create rocks around the island perimeter
const edgeRockCount = 40;
for (let i = 0; i < edgeRockCount; i++) {
  const angle = (i / edgeRockCount) * Math.PI * 2 + Math.random() * 0.2;

  // Place rocks near the edge with some variation
  const distVariation = (Math.random() - 0.5) * 6;
  const dist = groundRadius - 2 + distVariation;

  const x = Math.cos(angle) * dist;
  const z = Math.sin(angle) * dist;

  // Random rock size
  const scale = 0.8 + Math.random() * 2.0;

  const rockGeometry = new THREE.DodecahedronGeometry(scale, 0);
  const rockMesh = createCelMesh(rockGeometry, 0x808080); // Gray rocks
  rockMesh.position.set(x, scale * 0.3, z); // Partially buried
  rockMesh.rotation.set(
    Math.random() * Math.PI,
    Math.random() * Math.PI,
    Math.random() * Math.PI
  );
  scene.add(rockMesh);
}

// Sand beach is now integrated into heightmap terrain

// === OCEAN WATER AROUND ISLAND ===
// Wind Waker style ocean using same approach as pond water
const oceanGeometry = new THREE.PlaneGeometry(400, 400, 1, 1);
const oceanMaterial = new THREE.ShaderMaterial({
  uniforms: {
    time: { value: 0 },
    baseColor: { value: new THREE.Color(0x006994) },   // Deep ocean blue
    midColor: { value: new THREE.Color(0x0088aa) },    // Mid ocean blue
    lightColor: { value: new THREE.Color(0x00b4d8) },  // Light ocean blue
    waveScale: { value: 2.5 },  // Smaller waves (higher = more waves)
    islandRadius: { value: groundRadius },
    foamColor: { value: new THREE.Color(0xffffff) }
  },
  vertexShader: `
    varying vec2 vUv;
    varying vec3 vWorldPosition;
    uniform float time;

    void main() {
      vUv = uv;
      vec4 worldPos = modelMatrix * vec4(position, 1.0);
      vWorldPosition = worldPos.xyz;
      vec3 pos = position;
      // Very gentle wave motion for ocean
      float waveX = sin(position.x * 0.01 + time * 0.08) * 0.03;
      float waveZ = cos(position.y * 0.01 + time * 0.06) * 0.02;
      pos.z += waveX + waveZ;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    }
  `,
  fragmentShader: `
    varying vec2 vUv;
    varying vec3 vWorldPosition;
    uniform float time;
    uniform vec3 baseColor;
    uniform vec3 midColor;
    uniform vec3 lightColor;
    uniform float waveScale;
    uniform float islandRadius;
    uniform vec3 foamColor;

    float celShade(float value, float steps) {
      return floor(value * steps) / steps;
    }

    void main() {
      vec2 uv = vUv;

      // Ocean wave pattern - slower and smaller
      vec2 waveUv = uv * waveScale;
      waveUv.x += time * 0.008;  // Much slower
      waveUv.y -= time * 0.005;

      float waves = sin(waveUv.x * 12.0 + waveUv.y * 8.0) * 0.5 + 0.5;
      waves += cos(waveUv.x * 8.0 - waveUv.y * 10.0 + time * 0.02) * 0.25;

      // Wind Waker style cel-shading (3 distinct blue bands)
      float celShaded = celShade(waves, 3.0);

      vec3 waterColor;
      if (celShaded < 0.33) {
        waterColor = baseColor;
      } else if (celShaded < 0.66) {
        waterColor = midColor;
      } else {
        waterColor = lightColor;
      }

      // Foam appears when wave crests hit the shoreline - soft and diffuse
      float distFromCenter = length(vWorldPosition.xz);

      // Gradual shore proximity - wider foam zone
      float shoreProximity = smoothstep(islandRadius + 4.0, islandRadius - 0.5, distFromCenter);

      // Soft transition for wave crests (not hard step)
      float waveCrest = smoothstep(0.5, 0.75, celShaded);

      // Add some frothy variation based on position
      float angle = atan(vWorldPosition.z, vWorldPosition.x);
      float froth = sin(angle * 15.0 + time * 3.0) * 0.2 + 0.8;
      froth *= sin(distFromCenter * 2.0 + time * 2.5) * 0.15 + 0.85;

      // Foam gets stronger right at the edge
      float edgeBoost = smoothstep(islandRadius + 2.0, islandRadius, distFromCenter);

      // Combine: soft foam where waves meet shore
      float foamAmount = shoreProximity * waveCrest * froth * 0.85;
      foamAmount += edgeBoost * waveCrest * 0.3; // Extra foam right at edge

      foamAmount = clamp(foamAmount, 0.0, 1.0);

      // Mix foam with water color
      waterColor = mix(waterColor, foamColor, foamAmount);

      gl_FragColor = vec4(waterColor, 1.0);
    }
  `,
  transparent: false,
  side: THREE.DoubleSide
});
const ocean = new THREE.Mesh(oceanGeometry, oceanMaterial);
ocean.rotation.x = -Math.PI / 2;
ocean.position.y = -0.5; // Well below sand level so sand appears above water
scene.add(ocean);

// Store ocean for animation and fishing detection
window.gameOcean = ocean;

// Notify that ground has been created so cloud shadow texture can be applied
if (window.onGroundCreated) {
  window.onGroundCreated(ground);
}

// Increase the intensity of the main scene lights for better visibility
ambientLight.intensity = 1.0;
directionalLight.intensity = 1.2;

// Add a fill light to reduce shadows
const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
fillLight.position.set(-20, 30, -20); // Opposite direction from main light
scene.add(fillLight);

// Add some trees and rocks for decoration
function addEnvironmentObjects() {
  // Add trees - expanded positions for larger map
  const treePositions = [
    { x: -15, z: -10 },
    { x: 15, z: 10 },
    { x: -10, z: 15 },
    { x: 12, z: -12 },
    { x: -5, z: -15 },
    { x: 8, z: 15 },
    { x: -25, z: -20 },
    { x: 25, z: 20 },
    { x: -20, z: 25 },
    { x: 22, z: -22 },
    { x: -30, z: 5 },
    { x: 30, z: -15 },
    { x: -40, z: -30 },
    { x: 35, z: 35 },
    { x: 15, z: -35 },
    { x: -25, z: 40 }
  ];
  
  // Create single cloud-shaped shadow texture (pre-rendered, no overlapping)
  const treeShadowCanvas = document.createElement('canvas');
  treeShadowCanvas.width = 256;
  treeShadowCanvas.height = 256;
  const treeShadowCtx = treeShadowCanvas.getContext('2d');
  const cx = 128, cy = 128; // Center

  // Draw cloud shape - smaller circles, well centered
  const shadowCircles = [
    { x: cx, y: cy, r: 30 },
    { x: cx + 20, y: cy + 8, r: 24 },
    { x: cx - 22, y: cy + 6, r: 26 },
    { x: cx + 12, y: cy - 16, r: 20 },
    { x: cx - 14, y: cy - 14, r: 22 }
  ];

  // Draw soft shadow with multiple gradient layers
  for (let i = 0; i < 5; i++) {
    const alpha = 0.12; // More visible
    const spread = i * 5;
    shadowCircles.forEach(c => {
      const gradient = treeShadowCtx.createRadialGradient(c.x, c.y, 0, c.x, c.y, c.r + spread);
      gradient.addColorStop(0, `rgba(0, 0, 0, ${alpha})`);
      gradient.addColorStop(0.5, `rgba(0, 0, 0, ${alpha * 0.5})`);
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      treeShadowCtx.fillStyle = gradient;
      treeShadowCtx.beginPath();
      treeShadowCtx.arc(c.x, c.y, c.r + spread, 0, Math.PI * 2);
      treeShadowCtx.fill();
    });
  }

  const treeShadowTexture = new THREE.CanvasTexture(treeShadowCanvas);

  // Sun direction offset for shadows
  const sunOffsetX = 1.5;
  const sunOffsetZ = 1.5;

  treePositions.forEach(pos => {
    // Single shadow sprite per tree
    const treeShadow = new THREE.Sprite(new THREE.SpriteMaterial({
      map: treeShadowTexture,
      transparent: true,
      depthWrite: false,
      depthTest: false
    }));
    treeShadow.scale.set(10, 10, 1);
    treeShadow.position.set(pos.x + sunOffsetX, 0.3, pos.z + sunOffsetZ);
    treeShadow.renderOrder = -1; // Render before terrain
    scene.add(treeShadow);

    // Tree trunk
    const trunkGeometry = new THREE.CylinderGeometry(0.5, 0.7, 3, 8);
    const trunkMesh = createCelMesh(trunkGeometry, 0xCDAF95); // Keeping existing pastel trunk color
    trunkMesh.position.set(pos.x, 1.5, pos.z);
    trunkMesh.castShadow = true;
    scene.add(trunkMesh);
    
    // Tree top (leaves) - using a cluster of spheres
    const leavesGroup = new THREE.Group();
    leavesGroup.position.set(pos.x, 4.5, pos.z); // Base position for the leaves cluster

    const leafColor = 0xA2C49D; // Tree Leaves: Slightly deeper, muted tone (#A2C49D)
    const sphereRadii = [1.5, 1.2, 1.3, 1.0]; // Different sizes for the spheres
    const spherePositions = [
      new THREE.Vector3(0, 0.5, 0),    // Central, slightly larger sphere
      new THREE.Vector3(0.8, 0.2, 0.5),  // Offset spheres
      new THREE.Vector3(-0.7, 0.1, -0.6),
      new THREE.Vector3(0.5, 0, 0.9),
      new THREE.Vector3(-0.6, -0.1, 0.7) // Adding a fifth sphere for more volume
    ];

    spherePositions.forEach((spherePos, index) => {
      const radius = sphereRadii[index % sphereRadii.length]; // Cycle through radii if more positions than radii
      const leavesSphereGeometry = new THREE.SphereGeometry(radius, 8, 6); // Lower segments for stylized look
      const leavesSphereMesh = createCelMesh(leavesSphereGeometry, leafColor);
      leavesSphereMesh.position.copy(spherePos);
      leavesSphereMesh.castShadow = true;
      leavesGroup.add(leavesSphereMesh);
    });
    
    scene.add(leavesGroup);
    
    // Add fallen leaves on the ground around this tree - only if enabled in config
    // Create global array to track all ground leaves if it doesn't exist yet
    if (!window.groundLeaves) {
      window.groundLeaves = [];
    }
    
    if (gameConfig.showGroundLeaves) {
      const leafCount = 20 + Math.floor(Math.random() * 15); // 20-35 leaves per tree
      const leafRadius = 4; // More concentrated scatter radius
    
      for (let i = 0; i < leafCount; i++) {
        // Random position within radius, but not too close to trunk
        const angle = Math.random() * Math.PI * 2;
        const distance = 1 + Math.random() * leafRadius; // Start 1 unit away from trunk
        const leafX = pos.x + Math.cos(angle) * distance;
        const leafZ = pos.z + Math.sin(angle) * distance;
        
        // Create a leaf on the ground - use simple geometries
        const leafType = Math.floor(Math.random() * 3);
        let leafGeometry;
        
        if (leafType === 0) {
          // Oval leaf - smaller size
          leafGeometry = new THREE.CircleGeometry(0.15 + Math.random() * 0.15, 5);
        } else if (leafType === 1) {
          // Pointed leaf - smaller size
          leafGeometry = new THREE.ConeGeometry(0.1 + Math.random() * 0.1, 0.3, 5);
        } else {
          // Small cluster leaf - smaller size
          leafGeometry = new THREE.SphereGeometry(0.08 + Math.random() * 0.07, 4, 3);
        }
        
        // Vary the leaf colors - more brownish-green tones
        // Shift hue toward yellow/brown (0.1-0.25 range)
        const hue = 0.1 + (Math.random() * 0.15); 
        // Lower saturation for more muted colors
        const saturation = 0.4 + (Math.random() * 0.3); 
        // Darker for dried leaf appearance
        const lightness = 0.2 + (Math.random() * 0.25);
        
        const leafColor = new THREE.Color().setHSL(hue, saturation, lightness);
        const leafMesh = createCelMesh(leafGeometry, leafColor);
        
        // Position it right above the ground with random rotation
        leafMesh.position.set(leafX, 0.05, leafZ); // Just above ground level
        leafMesh.rotation.x = Math.PI/2 - (Math.random() * 0.5); // Mostly flat but slight variation
        leafMesh.rotation.y = Math.random() * Math.PI * 2;
        leafMesh.rotation.z = Math.random() * Math.PI * 2;
        
        // Make leaves even smaller with reduced scale
        const scale = 0.4 + Math.random() * 0.3; // Much smaller scale factor
        leafMesh.scale.set(scale, scale, scale * 0.1); // Extra flattened for leaf-like appearance
        
        leafMesh.receiveShadow = true;
        leafMesh.userData.isGroundLeaf = true; // Mark for identification
        scene.add(leafMesh);
        
        // Add to tracking array for later control
        window.groundLeaves.push(leafMesh);
      }
    }
  });
  
  // Add rocks - expanded positions for larger map
  const rockPositions = [
    { x: -12, z: 5, scale: 1.2 },
    { x: 7, z: -5, scale: 0.8 },
    { x: 14, z: 3, scale: 1.0 },
    { x: -3, z: -12, scale: 0.7 },
    { x: -22, z: 15, scale: 1.5 },
    { x: 17, z: -25, scale: 1.1 },
    { x: 24, z: 13, scale: 0.9 },
    { x: -33, z: -22, scale: 1.3 },
    { x: 28, z: 28, scale: 1.0 },
    { x: -18, z: 32, scale: 0.8 },
    { x: 38, z: -18, scale: 1.4 },
    { x: -40, z: 10, scale: 1.2 }
  ];
  
  rockPositions.forEach(pos => {
    const rockGeometry = new THREE.DodecahedronGeometry(pos.scale, 0);
    const rockMesh = createCelMesh(rockGeometry, 0x808080);
    rockMesh.position.set(pos.x, pos.scale / 2, pos.z);
    rockMesh.rotation.set(
      Math.random() * Math.PI,
      Math.random() * Math.PI,
      Math.random() * Math.PI
    );
    rockMesh.castShadow = true;
    rockMesh.receiveShadow = true;
    scene.add(rockMesh);
  });

  // Add bushes
  const bushPositions = [
    { x: -5, z: -8 },
    { x: 10, z: 12 },
    { x: -12, z: 18 },
    { x: 18, z: -15 },
    { x: 2, z: 5 },
    { x: -20, z: -5 },
    { x: 25, z: -2 },
    { x: -30, z: 15 },
    { x: 35, z: -25 },
    { x: -15, z: 30 }
  ];
  const bushColor = 0x88B378; // Bushes: Distinct, calm but visible (#88B378)

  bushPositions.forEach(pos => {
    const bushGroup = new THREE.Group();
    bushGroup.position.set(pos.x, 0.5, pos.z); // Position low to the ground

    const bushRadii = [0.8, 0.6, 0.7]; // Smaller spheres than trees
    const bushSpherePositions = [
      new THREE.Vector3(0, 0, 0),      // Central sphere
      new THREE.Vector3(0.5, 0, 0.2),  // Offset spheres
      new THREE.Vector3(-0.4, 0, -0.3),
      new THREE.Vector3(0.2, 0, 0.6)
    ];

    bushSpherePositions.forEach((spherePos, index) => {
      const radius = bushRadii[index % bushRadii.length];
      const bushSphereGeometry = new THREE.SphereGeometry(radius, 6, 5); // Fewer segments for bush
      const bushSphereMesh = createCelMesh(bushSphereGeometry, bushColor);
      bushSphereMesh.position.copy(spherePos);
      bushSphereMesh.castShadow = true;
      bushGroup.add(bushSphereMesh);
    });

    scene.add(bushGroup);
  });
}

addEnvironmentObjects();

// Create sand borders around each pond (fishingHolePositions defined at top of file)
createSandBorders();

// Create a very sunny gradient sky (performance-friendly)
const skyColors = {
  top: new THREE.Color(0x1976D2),    // Stronger deep blue at the top
  middle: new THREE.Color(0x2196F3), // Vibrant medium blue in the middle
  horizon: new THREE.Color(0x90CAF9), // Bright blue near horizon
  glow: new THREE.Color(0xFFCC80)    // More pronounced orange-gold sun glow
};

// Create a simple vertical gradient texture for the sky
const skyCanvas = document.createElement('canvas');
skyCanvas.width = 2;  // Very small for performance, will be stretched
skyCanvas.height = 128;
const skyCtx = skyCanvas.getContext('2d');
const skyGradient = skyCtx.createLinearGradient(0, 0, 0, 128);
skyGradient.addColorStop(0, '#' + skyColors.top.getHexString());
skyGradient.addColorStop(0.4, '#' + skyColors.middle.getHexString());
skyGradient.addColorStop(0.8, '#' + skyColors.horizon.getHexString());
skyGradient.addColorStop(1, '#' + skyColors.glow.getHexString());
skyCtx.fillStyle = skyGradient;
skyCtx.fillRect(0, 0, 2, 128);

// Create texture and set as background
const skyTexture = new THREE.CanvasTexture(skyCanvas);
skyTexture.minFilter = THREE.LinearFilter; // Better quality when stretched
skyTexture.magFilter = THREE.LinearFilter;
scene.background = skyTexture;

fishingHolePositions.forEach((position, index) => {
  const holeGeometry = new THREE.CircleGeometry(2, 32);
  // Create water geometry for the fishing hole
  const waterGeometry = new THREE.CircleGeometry(2, 32);
  
  // Generate unique wave pattern variations for each pond (but use consistent colors)
  // Use consistent blue colors across all ponds
  const baseColor = new THREE.Color(0x44aaff); // Vibrant Wind Waker blue
  const midColor = new THREE.Color(0x77bbff);   // Mid-tone blue
  const lightColor = new THREE.Color(0x99ccff); // Lighter blue highlight
  
  // Generate unique pattern variations
  const waveScale = 3.8 + (index % 3) * 0.8; // Different wave scaling
  const timeOffset = index * 0.5; // Each pond starts at different animation phase
  
  // Wind Waker styled cel-shaded water material
  const waterMaterial = new THREE.ShaderMaterial({
    uniforms: {
      time: { value: timeOffset },
      baseColor: { value: baseColor }, // Same blue for all ponds
      midColor: { value: midColor },   // Same mid-tone for all ponds
      lightColor: { value: lightColor }, // Same highlight for all ponds
      waveScale: { value: waveScale }, // Unique wave scale per pond
      pondSeed: { value: index } // Seed value to create variation
    },
    vertexShader: `
      varying vec2 vUv;
      uniform float time;
      uniform float waveScale;
      uniform float pondSeed;
      
      void main() {
        vUv = uv;
        // Simple wave motion with Wind Waker style - varied by pond
        vec3 pos = position;
        float seed = pondSeed * 0.1; // Create variation based on pond index
        
        // Slowed down wave motion for gentle pond-like movement
        float waveX = sin(position.x * (2.8 + seed) + time * (0.4 + seed * 0.1)) * 0.05;
        float waveZ = cos(position.z * (2.2 + seed) + time * (0.3 + seed * 0.1)) * 0.03;
        pos.y += waveX + waveZ;
        
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `,
    fragmentShader: `
      varying vec2 vUv;
      uniform float time;
      uniform vec3 baseColor;    // Main Wind Waker Blue
      uniform vec3 midColor;     // Mid-tone Blue
      uniform vec3 lightColor;   // Lighter Blue
      uniform float waveScale;   // Scale of wave pattern, unique per pond
      uniform float pondSeed;    // Seed value to create variation

      // Cel-shading helper function
      float celShade(float value, float steps) {
        return floor(value * steps) / steps;
      }

      float rand(vec2 co) {
          return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
      }

      void main() {
          vec2 uv = vUv;
          float seed = pondSeed * 0.1; // Create variation factor

          // --- Base Wave Pattern (with pond variations) - slowed down ---
          vec2 waveUv = uv * (waveScale + rand(vec2(seed)) * 0.5);
          waveUv.x += time * (0.12 + seed * 0.02); // Reduced speed by ~60%
          waveUv.y -= time * (0.08 - seed * 0.01); // Reduced speed by ~60%
          
          float waves = sin(waveUv.x + waveUv.y + seed) * 0.5 + 0.5;
          waves += cos(waveUv.x * (0.8 + seed * 0.2) - waveUv.y * (1.3 - seed * 0.1) + time * 0.2) * 0.3; // Slowed down from 0.5 to 0.2
          
          // --- Circular Ripples (with pond variations) - slowed down ---
          float rippleSpeed = 0.8 + seed * 0.4; // Slowed down from 2.0 to 0.8
          float dist = length(uv - 0.5) * (8.0 - seed);
          float ripple = sin(dist - time * rippleSpeed) * (0.1 + seed * 0.02);
          
          // Combine waves and ripples
          float pattern = waves + ripple;
          
          // Wind Waker style cel-shading (3 distinct blue bands)
          float celShaded = celShade(pattern, 3.0);
          
          // Create three-tone blue water using cel-shaded value
          vec3 waterColor;
          if (celShaded < 0.33) {
            waterColor = baseColor;
          } else if (celShaded < 0.66) {
            waterColor = midColor;
          } else {
            waterColor = lightColor;
          }
          
          // Subtle edge transition to slightly lighter blue (not white)
          float edge = smoothstep(0.46, 0.5, length(uv - 0.5));
          waterColor = mix(waterColor, lightColor, edge * 0.6);
          
          gl_FragColor = vec4(waterColor, 1.0);
      }
    `,
    transparent: true
  });
  
  const waterMesh = new THREE.Mesh(waterGeometry, waterMaterial);
  waterMesh.rotation.x = -Math.PI / 2;
  waterMesh.position.set(position.x, 0.02, position.z); // Pond water level (terrain has depression at -0.1)
  
  // Green rim around the water has been removed
  
  scene.add(waterMesh);
  // No rim
  
  // Create locked overlay for ponds that aren't unlocked yet
  const lockedOverlayGeometry = new THREE.CircleGeometry(2.1, 32);
  const lockedOverlayMaterial = new THREE.MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.5,
    side: THREE.DoubleSide
  });
  const lockedOverlay = new THREE.Mesh(lockedOverlayGeometry, lockedOverlayMaterial);
  lockedOverlay.rotation.x = -Math.PI / 2;
  lockedOverlay.position.set(position.x, 0.12, position.z);
  lockedOverlay.visible = !progressionSystem.isPondUnlocked(index);
  scene.add(lockedOverlay);

  // Create lock icon sprite for locked ponds
  const lockCanvas = document.createElement('canvas');
  lockCanvas.width = 64;
  lockCanvas.height = 64;
  const lockCtx = lockCanvas.getContext('2d');
  // Draw lock emoji (canvas is transparent by default)
  lockCtx.font = '48px Arial';
  lockCtx.textAlign = 'center';
  lockCtx.textBaseline = 'middle';
  lockCtx.fillStyle = '#000000';
  lockCtx.fillText('🔒', 32, 32);
  const lockTexture = new THREE.CanvasTexture(lockCanvas);
  const lockMaterial = new THREE.SpriteMaterial({
    map: lockTexture,
    transparent: true,
    alphaTest: 0.1
  });
  const lockSprite = new THREE.Sprite(lockMaterial);
  lockSprite.position.set(position.x, 1.5, position.z);
  lockSprite.scale.set(1.5, 1.5, 1);
  lockSprite.visible = !progressionSystem.isPondUnlocked(index);
  scene.add(lockSprite);

  // Create tier indicator ring around the pond
  const tierColors = {
    tier1: 0x3498db, // Blue for starter ponds (common/uncommon)
    tier2: 0x2ecc71, // Green for mid-tier ponds (uncommon/rare)
    tier3: 0x9b59b6  // Purple for end-game ponds (rare/legendary)
  };
  const tier = index <= 3 ? 'tier1' : (index <= 5 ? 'tier2' : 'tier3');
  const tierRingGeometry = new THREE.RingGeometry(2.0, 2.25, 32);
  const tierRingMaterial = new THREE.MeshBasicMaterial({
    color: tierColors[tier],
    transparent: true,
    opacity: 0.8,
    side: THREE.DoubleSide
  });
  const tierIndicator = new THREE.Mesh(tierRingGeometry, tierRingMaterial);
  tierIndicator.rotation.x = -Math.PI / 2;
  tierIndicator.position.set(position.x, 0.06, position.z);
  scene.add(tierIndicator);

  // Create shimmer effect for rare ponds (tier 3: indices 6, 7)
  let shimmerMesh = null;
  if (index >= 6) {
    const shimmerGeometry = new THREE.CircleGeometry(2.3, 32);
    const shimmerMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        color: { value: new THREE.Color(index === 7 ? 0x9b59b6 : 0x3498db) } // Purple for legendary, blue for rare
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform vec3 color;
        varying vec2 vUv;
        void main() {
          float dist = length(vUv - 0.5) * 2.0;
          float ring = smoothstep(0.85, 0.9, dist) * smoothstep(1.0, 0.95, dist);
          float shimmer = sin(time * 3.0 + dist * 10.0) * 0.5 + 0.5;
          float alpha = ring * shimmer * 0.6;
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      side: THREE.DoubleSide
    });
    shimmerMesh = new THREE.Mesh(shimmerGeometry, shimmerMaterial);
    shimmerMesh.rotation.x = -Math.PI / 2;
    shimmerMesh.position.set(position.x, 0.08, position.z);
    shimmerMesh.visible = progressionSystem.isPondUnlocked(index);
    scene.add(shimmerMesh);
  }

  fishingHoles.push({
    index: index, // Store the index for variation purposes
    position: new THREE.Vector3(position.x, 0, position.z),
    water: waterMesh,
    lockedOverlay: lockedOverlay, // Reference to locked overlay
    lockSprite: lockSprite, // Reference to lock icon sprite
    shimmerMesh: shimmerMesh, // Reference to shimmer effect (null for non-rare ponds)
    tierIndicator: tierIndicator, // Reference to tier indicator
    tier: tier, // Pond tier (tier1, tier2, tier3)
    fishChance: 0.5 + (Math.random() * 0.3), // Random chance of catching fish
    fishingActive: false,
    fishShadows: [] // Will be populated below
  });
});

// === FISH SHADOWS IN PONDS ===
// Create fish shadow texture
const fishShadowCanvas = document.createElement('canvas');
fishShadowCanvas.width = 32;
fishShadowCanvas.height = 16;
const fishShadowCtx = fishShadowCanvas.getContext('2d');

// Draw simple fish silhouette
fishShadowCtx.fillStyle = 'rgba(0, 30, 60, 0.5)';
fishShadowCtx.beginPath();
// Fish body (ellipse)
fishShadowCtx.ellipse(14, 8, 10, 5, 0, 0, Math.PI * 2);
fishShadowCtx.fill();
// Fish tail
fishShadowCtx.beginPath();
fishShadowCtx.moveTo(24, 8);
fishShadowCtx.lineTo(31, 3);
fishShadowCtx.lineTo(31, 13);
fishShadowCtx.closePath();
fishShadowCtx.fill();

const fishShadowTexture = new THREE.CanvasTexture(fishShadowCanvas);

// Add fish shadows to each pond
fishingHoles.forEach((hole, holeIndex) => {
  const numFish = 2 + Math.floor(Math.random() * 3); // 2-4 fish per pond
  const pondRadius = 1.8; // Stay within pond bounds

  for (let i = 0; i < numFish; i++) {
    const fishSprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: fishShadowTexture,
      transparent: true,
      depthWrite: false
    }));

    // Random size variation
    const scale = 0.4 + Math.random() * 0.3;
    fishSprite.scale.set(scale, scale * 0.5, 1);

    // Random starting position within pond
    const angle = Math.random() * Math.PI * 2;
    const dist = Math.random() * pondRadius * 0.7;
    fishSprite.position.set(
      hole.position.x + Math.cos(angle) * dist,
      0.02, // Just above pond bottom
      hole.position.z + Math.sin(angle) * dist
    );

    // Store animation data
    fishSprite.userData = {
      angle: angle, // Current swim direction
      speed: 0.3 + Math.random() * 0.4, // Swim speed
      turnTimer: Math.random() * 3, // Time until next direction change
      pondCenter: hole.position.clone(),
      pondRadius: pondRadius
    };

    scene.add(fishSprite);
    hole.fishShadows.push(fishSprite);
  }
});

// Function to update fish shadow positions
function updateFishShadows(deltaTime) {
  fishingHoles.forEach(hole => {
    hole.fishShadows.forEach(fish => {
      const data = fish.userData;

      // Update turn timer
      data.turnTimer -= deltaTime;
      if (data.turnTimer <= 0) {
        // Change direction randomly
        data.angle += (Math.random() - 0.5) * Math.PI * 0.8;
        data.turnTimer = 1 + Math.random() * 3;
      }

      // Move fish
      const moveX = Math.cos(data.angle) * data.speed * deltaTime;
      const moveZ = Math.sin(data.angle) * data.speed * deltaTime;
      fish.position.x += moveX;
      fish.position.z += moveZ;

      // Keep fish within pond bounds
      const dx = fish.position.x - data.pondCenter.x;
      const dz = fish.position.z - data.pondCenter.z;
      const distFromCenter = Math.sqrt(dx * dx + dz * dz);

      if (distFromCenter > data.pondRadius) {
        // Turn back toward center
        data.angle = Math.atan2(-dz, -dx) + (Math.random() - 0.5) * 0.5;
        // Push back inside
        const scale = data.pondRadius / distFromCenter;
        fish.position.x = data.pondCenter.x + dx * scale * 0.9;
        fish.position.z = data.pondCenter.z + dz * scale * 0.9;
      }

      // Rotate sprite to face movement direction
      fish.material.rotation = -data.angle + Math.PI / 2;
    });
  });
}

// Create player character from GLB model with animations
let playerMixer = null;
let playerWalkAction = null;
let playerIdleAction = null;
const initialTerrainHeight = getTerrainHeightAt(0, 0);

// Create temporary placeholder while model loads
const playerGeometry = new THREE.CapsuleGeometry(0.5, 1, 4, 8);
player = createCelMesh(playerGeometry, 0x3f51b5);
player.position.set(0, Math.max(initialTerrainHeight, 0) + 1, 0);
player.castShadow = true;
player.visible = false; // Hide placeholder
scene.add(player);

// Load animated character model (GLTF with textures)
gltfLoader.load('/models/boy_character/scene.gltf', (gltf) => {
  const model = gltf.scene;

  // Scale and position the model
  model.scale.set(1, 1, 1); // Adjust scale as needed
  model.position.copy(player.position);
  model.position.y = getTerrainHeightAt(0, 0);

  // Manually load textures (GLTF uses unsupported extension)
  const textureLoader = new THREE.TextureLoader();
  const bodyTexture = textureLoader.load('/models/boy_character/textures/Boy01_Body_MAT1_diffuse.jpeg');
  const mouthTexture = textureLoader.load('/models/boy_character/textures/Boy01_Mouth_MAT2_diffuse.png');

  // Flip textures if needed
  bodyTexture.flipY = false;
  mouthTexture.flipY = false;

  model.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;

      const matName = (child.material.name || '').toLowerCase();

      if (matName.includes('body')) {
        child.material = new THREE.MeshToonMaterial({
          map: bodyTexture,
          side: THREE.DoubleSide
        });
      } else if (matName.includes('mouth') || matName.includes('eye')) {
        child.material = new THREE.MeshToonMaterial({
          map: mouthTexture,
          transparent: true,
          side: THREE.DoubleSide
        });
      } else if (matName.includes('brow')) {
        child.material = new THREE.MeshToonMaterial({
          map: mouthTexture,
          transparent: true,
          side: THREE.DoubleSide
        });
      }
    }
  });

  // Remove placeholder and use the model
  scene.remove(player);
  player = model;
  scene.add(player);

  // Set up animation mixer
  playerMixer = new THREE.AnimationMixer(model);

  // Find animations
  if (gltf.animations.length > 0) {
    console.log('Player animations found:', gltf.animations.map(a => a.name));

    // Try to find walk/idle animations by name
    gltf.animations.forEach(clip => {
      const name = clip.name.toLowerCase();
      if (name.includes('walk') || name.includes('run')) {
        playerWalkAction = playerMixer.clipAction(clip);
        playerWalkAction.setLoop(THREE.LoopRepeat);
      } else if (name.includes('idle') || name.includes('stand')) {
        playerIdleAction = playerMixer.clipAction(clip);
        playerIdleAction.setLoop(THREE.LoopRepeat);
      }
    });

    // If no specific animations found, use first as walk
    if (!playerWalkAction && gltf.animations.length > 0) {
      playerWalkAction = playerMixer.clipAction(gltf.animations[0]);
      playerWalkAction.setLoop(THREE.LoopRepeat);
    }

    // Walk animation speed
    if (playerWalkAction) {
      playerWalkAction.timeScale = 1.0; // Normal speed
    }

    // Start with idle or stopped
    if (playerIdleAction) {
      playerIdleAction.play();
    }
  }

  console.log('Player model loaded successfully');
}, undefined, (error) => {
  console.error('Error loading player model:', error);
  // Keep placeholder visible if model fails to load
  player.visible = true;
});

// Simple blob shadow under player (Wind Waker style - soft gradient)
const shadowCanvas = document.createElement('canvas');
shadowCanvas.width = 64;
shadowCanvas.height = 64;
const shadowCtx = shadowCanvas.getContext('2d');
const gradient = shadowCtx.createRadialGradient(32, 32, 0, 32, 32, 32);
gradient.addColorStop(0, 'rgba(0, 0, 0, 0.4)');
gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.2)');
gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
shadowCtx.fillStyle = gradient;
shadowCtx.fillRect(0, 0, 64, 64);
const shadowTexture = new THREE.CanvasTexture(shadowCanvas);
const playerShadow = new THREE.Sprite(new THREE.SpriteMaterial({
  map: shadowTexture,
  transparent: true,
  depthWrite: false
}));
playerShadow.scale.set(2, 2, 1);
playerShadow.position.set(0, initialTerrainHeight + 0.02, 0);
playerShadow.material.rotation = 0;
scene.add(playerShadow);

// Add a walking indicator
const indicatorGeometry = new THREE.CircleGeometry(0.3, 16);
const indicatorMaterial = new THREE.MeshBasicMaterial({ 
  color: 0xffff00,
  transparent: true,
  opacity: 0.7
});
const walkIndicator = new THREE.Mesh(indicatorGeometry, indicatorMaterial);
walkIndicator.rotation.x = -Math.PI / 2;
walkIndicator.position.y = 0.05;
walkIndicator.visible = false;
scene.add(walkIndicator);

// Create the house
gameState.house = createHouse(scene, new THREE.Vector3(20, 0, 20));

// Create the house interior (initially hidden)
gameState.houseInterior = createHouseInterior(scene);

// Create the iris wipe effect
gameState.irisEffect = createIrisWipeEffect();

// Function to handle entering the house
function enterHouse() {
  if (gameState.isTransitioning || gameState.isInHouse) return;
  
  // Set the transition flag
  gameState.isTransitioning = true;
  
  // Show transition message
  const transitionMessage = document.getElementById('transition-message');
  transitionMessage.textContent = 'Entering house...';
  transitionMessage.style.display = 'block';
  
  // Save the current camera position and rotation
  const savedCameraPosition = camera.position.clone();
  const savedCameraRotation = camera.rotation.clone();
  
  // Start the transition
  transitionToInterior(renderer, scene, camera, gameState.houseInterior, gameState.irisEffect, () => {
    gameState.isInHouse = true;
    gameState.isTransitioning = false;
    
    // Move player mesh from main scene to interior group
    scene.remove(player);
    gameState.houseInterior.add(player);

    // Move shadow to interior
    if (playerShadow) {
      scene.remove(playerShadow);
      gameState.houseInterior.add(playerShadow);
    }

    // Change camera to top-down view for Zelda-like perspective
    // Position player inside the house near the door
    player.position.set(0, 1, 5);
    player.rotation.y = Math.PI; // Face away from the door

    // Update shadow position
    if (playerShadow) {
      playerShadow.position.set(0, 0.02, 5);
    }

    // Camera will automatically follow player with interior settings
    
    // Show exit indicator
    document.getElementById('exit-indicator').style.display = 'block';
    
    // Hide transition message
    transitionMessage.style.display = 'none';
  });
}

// Function to handle exiting the house
function exitHouse() {
  if (gameState.isTransitioning || !gameState.isInHouse) return;
  
  // Set the transition flag
  gameState.isTransitioning = true;
  
  // Show transition message
  const transitionMessage = document.getElementById('transition-message');
  transitionMessage.textContent = 'Exiting house...';
  transitionMessage.style.display = 'block';
  
  // Start the transition
  transitionToExterior(renderer, scene, camera, gameState.houseInterior, gameState.irisEffect, () => {
    // After transition completes
    gameState.isInHouse = false;
    gameState.isTransitioning = false;

    // Move player mesh from interior group back to main scene
    gameState.houseInterior.remove(player);
    scene.add(player);

    // Move shadow back to main scene
    if (playerShadow) {
      gameState.houseInterior.remove(playerShadow);
      scene.add(playerShadow);
    }

    // Camera will automatically follow player with exterior settings

    // Position player outside the house
    const doorPosition = new THREE.Vector3().copy(gameState.house.position);
    doorPosition.z += 3; // A bit further from the door
    player.position.copy(doorPosition);
    // Set player height based on terrain
    const exitTerrainHeight = getTerrainHeightAt(doorPosition.x, doorPosition.z);
    player.position.y = exitTerrainHeight + 1;
    player.rotation.y = Math.PI; // Face away from the house

    // Update shadow position
    if (playerShadow) {
      playerShadow.position.x = player.position.x;
      playerShadow.position.z = player.position.z;
      playerShadow.position.y = exitTerrainHeight + 0.02;
    }
    
    // Hide exit indicator
    document.getElementById('exit-indicator').style.display = 'none';
    
    // Hide transition message
    transitionMessage.style.display = 'none';
  });
}

// Move player to target position
function movePlayerToPosition(targetPosition) {
  if (gameState.isFishing || gameState.isTransitioning) return; // Don't move while fishing or transitioning
  
  gameState.playerMoving = true;
  gameState.playerTargetPosition = targetPosition;
  
  // Show indicator at target position (follow terrain height)
  walkIndicator.position.x = targetPosition.x;
  walkIndicator.position.z = targetPosition.z;
  if (ground && !gameState.isInHouse) {
    walkIndicator.position.y = getTerrainHeightAt(targetPosition.x, targetPosition.z) + 0.05;
  }
  walkIndicator.visible = true;
  
  // Create a small animation for the indicator
  const pulseAnimation = () => {
    if (!gameState.playerMoving) return;
    
    walkIndicator.scale.x = 1 + Math.sin(Date.now() / 200) * 0.2;
    walkIndicator.scale.z = 1 + Math.sin(Date.now() / 200) * 0.2;
    
    requestAnimationFrame(pulseAnimation);
  };
  
  pulseAnimation();
}

// Update player movement - using fixed time step for true frame rate independence
function updatePlayerMovement() {
  // Always using our fixed delta time from the game loop
  // This ensures all devices move at exactly the same speed
  const deltaTime = window.currentDeltaTime || 0.016; // Fallback to ~60fps if not available
  
  if (!gameState.playerMoving || !gameState.playerTargetPosition) return;
  
  // Calculate direction and distance
  const direction = new THREE.Vector3().subVectors(gameState.playerTargetPosition, player.position);
  direction.y = 0; // Keep y position constant (don't fly or sink)
  
  const distance = direction.length();
  
  // If close enough to target, stop moving
  if (distance < 0.1) {
    gameState.playerMoving = false;
    gameState.playerTargetPosition = null;
    if (walkIndicator) walkIndicator.visible = false;

    // Pause walk animation at first frame (standing pose)
    if (playerWalkAction) {
      playerWalkAction.paused = true;
      playerWalkAction.time = 0;
    }
    
    // Check if we have pending ocean fishing
    if (gameState.pendingOceanFishing) {
      const oceanHole = gameState.pendingOceanFishing;
      gameState.pendingOceanFishing = null;
      startFishing(oceanHole);
      return;
    }

    // Check if we've reached the house door
    if (!gameState.isInHouse) {
      const doorPosition = new THREE.Vector3().copy(gameState.house.position);
      doorPosition.z += 2; // Door is at the front

      const distanceToDoor = player.position.distanceTo(doorPosition);
      if (distanceToDoor < 2) {
        // We're at the door, enter the house
        enterHouse();
      }
    } else {
      // Check if we're at the exit door in the interior
      if (player.position.z > 3.5) { // Near the front door
        exitHouse();
      }
    }

    return;
  }
  
  // Zelda-style movement: turn toward target, then move FORWARD
  const BASE_SPEED = 6.5; // Balanced speed to match walk animation
  const frameIndependentSpeed = BASE_SPEED * FIXED_DELTA_TIME;

  // Calculate target angle
  const moveDirection = direction.clone().normalize();
  const targetAngle = Math.atan2(moveDirection.x, moveDirection.z);

  // Smooth rotation toward target
  let angleDiff = targetAngle - player.rotation.y;
  while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
  while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

  const turnSpeed = 8 * FIXED_DELTA_TIME;
  player.rotation.y += angleDiff * Math.min(turnSpeed, 1);

  // Move FORWARD in the direction player is FACING (not directly to target)
  const forwardX = Math.sin(player.rotation.y);
  const forwardZ = Math.cos(player.rotation.y);

  // Reduce speed when turning sharply (Zelda-style)
  const facingAlignment = Math.cos(angleDiff); // 1 when facing target, -1 when opposite
  const speedMultiplier = Math.max(0.3, facingAlignment); // Min 30% speed when turning

  player.position.x += forwardX * frameIndependentSpeed * speedMultiplier;
  player.position.z += forwardZ * frameIndependentSpeed * speedMultiplier;

  // Play/unpause walk animation
  if (playerWalkAction) {
    if (!playerWalkAction.isRunning()) {
      playerWalkAction.play();
    }
    playerWalkAction.paused = false;
  }

  // Constrain player to visible terrain bounds (prevent walking on water)
  if (!gameState.isInHouse) {
    const distFromCenter = Math.sqrt(player.position.x * player.position.x + player.position.z * player.position.z);
    const maxWalkableRadius = groundRadius - 7; // Stay well within visible terrain

    if (distFromCenter > maxWalkableRadius) {
      // Clamp position to edge of walkable area
      const scale = maxWalkableRadius / distFromCenter;
      player.position.x *= scale;
      player.position.z *= scale;

      // Stop movement since we hit the edge
      gameState.playerMoving = false;
      gameState.playerTargetPosition = null;
      if (walkIndicator) walkIndicator.visible = false;

      // Pause walk animation at first frame (standing pose)
      if (playerWalkAction) {
        playerWalkAction.paused = true;
        playerWalkAction.time = 0;
      }

      // If we have pending ocean fishing, start it now
      if (gameState.pendingOceanFishing) {
        const oceanHole = gameState.pendingOceanFishing;
        gameState.pendingOceanFishing = null;
        startFishing(oceanHole);
      }
    }
  }

  // Update player Y position to follow terrain height
  if (ground && !gameState.isInHouse) {
    const terrainHeight = getTerrainHeightAt(player.position.x, player.position.z);
    player.position.y = terrainHeight; // Model origin is at feet
  }

  // Update shadow position (follow terrain)
  if (playerShadow) {
    playerShadow.position.x = player.position.x;
    playerShadow.position.z = player.position.z;
    if (ground && !gameState.isInHouse) {
      playerShadow.position.y = getTerrainHeightAt(player.position.x, player.position.z) + 0.02;
    }
  }

  // Make player face movement direction
  if (direction.length() > 0) {
    const angle = Math.atan2(direction.x, direction.z);
    player.rotation.y = angle;
  }
}

// Create fishing rod (when needed)
function createFishingRod(position) {
  // Remove existing fishing rod if any
  if (gameState.fishingRod) {
    scene.remove(gameState.fishingRod);
    scene.remove(gameState.fishingLine);
    scene.remove(gameState.fishingFloat);
  }
  
  // Rod
  const rodGeometry = new THREE.CylinderGeometry(0.05, 0.03, 3, 8);
  const rod = createCelMesh(rodGeometry, 0x8d6e63);
  rod.position.copy(position);
  rod.position.y += 1.5;
  rod.rotation.x = Math.PI / 4;
  rod.castShadow = true;
  scene.add(rod);
  
  // Fishing line
  const lineGeometry = new THREE.BufferGeometry();
  const lineMaterial = new THREE.LineBasicMaterial({ color: 0xffffff });
  
  // Calculate line end position (in the water)
  const lineEndPosition = new THREE.Vector3().copy(gameState.activeFishingHole.position);
  
  const linePoints = [
    new THREE.Vector3(position.x, position.y + 3, position.z),
    lineEndPosition
  ];
  
  lineGeometry.setFromPoints(linePoints);
  const line = new THREE.Line(lineGeometry, lineMaterial);
  scene.add(line);
  
  // Fishing float
  const floatGeometry = new THREE.SphereGeometry(0.15, 16, 16);
  const floatMaterial = new THREE.MeshStandardMaterial({ 
    color: 0xff0000,
    roughness: 0.5,
    metalness: 0.2
  });
  const float = new THREE.Mesh(floatGeometry, floatMaterial);
  float.position.copy(lineEndPosition);
  float.position.y += 0.1; // Float on water
  float.castShadow = true;
  scene.add(float);
  
  gameState.fishingRod = rod;
  gameState.fishingLine = line;
  gameState.fishingFloat = float;
}

// --- New Horizontal Fishing Minigame Implementation ---
horizontalMinigame = {
  isActive: false,
  ballPosition: 0, // Percentage from left (0 to 100)
  ballDirection: 1, // 1 for right, -1 for left
  ballSpeed: 2, // Percentage per frame, adjust for difficulty
  targetZoneStart: 40, // Start of target zone (percentage)
  targetZoneEnd: 60,   // End of target zone (percentage)
  animationFrame: null,
  container: null,
  ballElement: null,
  barElement: null,
  chances: 3, // Player gets a few chances
  maxChances: 3,
  hitSuccess: false, // Tracks if the current attempt was a success,
  timeAccumulator: 0, // Time accumulator for frame-rate independence
  lastTime: 0 // Last timestamp for calculating delta
};

function startHorizontalMinigame(fishRarity = 'common') {
  horizontalMinigame.isActive = true;
  horizontalMinigame.ballPosition = Math.random() * 20; // Start at a random near-left position
  horizontalMinigame.ballDirection = 1;
  horizontalMinigame.chances = horizontalMinigame.maxChances;
  horizontalMinigame.hitSuccess = false;
  
  // Adjust difficulty based on fish rarity
  let baseSpeed;
  let targetZoneWidth; // How wide the green zone is (smaller = harder)
  
  switch(fishRarity) {
    case 'legendary':
      baseSpeed = 3.0; // Extremely fast for legendary fish
      targetZoneWidth = 10; // Very narrow target zone (much harder)
      break;
    case 'rare':
      baseSpeed = 2.2; // Very fast for rare fish
      targetZoneWidth = 14; // Narrow target zone
      break;
    case 'uncommon':
      baseSpeed = 1.6; // Medium-fast for uncommon fish
      targetZoneWidth = 18; // Standard target zone
      break;
    default: // 'common'
      baseSpeed = 1.2; // Slower but still challenging for common fish
      targetZoneWidth = 22; // Wider target zone (easier)
  }
  
  // Calculate target zone start/end based on width
  const centerPoint = 50; // Center of the bar (percent)
  const halfWidth = targetZoneWidth / 2;
  horizontalMinigame.targetZoneStart = centerPoint - halfWidth;
  horizontalMinigame.targetZoneEnd = centerPoint + halfWidth;
  
  // Add some randomness to keep it interesting, but less than before
  // to maintain consistent difficulty by rarity
  horizontalMinigame.ballSpeed = baseSpeed + Math.random() * 0.4;
  // Initialize time tracking variables for frame-rate independence
  horizontalMinigame.timeAccumulator = 0;
  horizontalMinigame.lastTime = performance.now();


  horizontalMinigame.container = document.getElementById('horizontal-minigame-container');
  horizontalMinigame.ballElement = document.getElementById('minigame-ball');
  horizontalMinigame.barElement = document.getElementById('minigame-bar');
  const instructionElement = document.getElementById('minigame-instruction');

  if (!horizontalMinigame.container || !horizontalMinigame.ballElement || !horizontalMinigame.barElement || !instructionElement) {
    console.error("Minigame UI elements not found!");
    endHorizontalMinigame(false); 
    return;
  }
  // Update instruction text to include touch option
  instructionElement.textContent = `Press SPACE or TAP screen in the green zone! Chances: ${horizontalMinigame.chances}`;
  horizontalMinigame.container.style.display = 'flex';
  updateHorizontalMinigameUI();

  // Add keyboard and touch event listeners
  window.addEventListener('keydown', horizontalMinigameKeyDown);
  horizontalMinigame.container.addEventListener('touchstart', horizontalMinigameTouchHandler);

  horizontalMinigame.animationFrame = requestAnimationFrame(horizontalMinigameLoop);
}

function endHorizontalMinigame(success) {
  horizontalMinigame.isActive = false;
  if (horizontalMinigame.container) {
    horizontalMinigame.container.style.display = 'none';
  }
  window.removeEventListener('keydown', horizontalMinigameKeyDown);
  horizontalMinigame.container.removeEventListener('touchstart', horizontalMinigameTouchHandler);

  if (horizontalMinigame.animationFrame) {
    cancelAnimationFrame(horizontalMinigame.animationFrame);
    horizontalMinigame.animationFrame = null;
  }

  //gameState.fishBiting = false; // Should be reset here

  if (success) {
    catchFish(); 
  } else {
    endFishing(false); // This already shows "fish got away"
    // showNotification('The fish got away!', 1500, 'common'); // Redundant if endFishing shows it
  }
}

function horizontalMinigameKeyDown(e) {
  if (!horizontalMinigame.isActive || e.repeat) return;
  if (e.code === 'Space') {
    e.preventDefault();
    checkHorizontalMinigameHit();
  }
}

// Handle touch events for mobile devices
function horizontalMinigameTouchHandler(e) {
  if (!horizontalMinigame.isActive) return;
  e.preventDefault(); // Prevent scrolling or other touch actions
  checkHorizontalMinigameHit();
}

function checkHorizontalMinigameHit() {
  if (!horizontalMinigame.isActive) return;

  // Calculate the center of the ball relative to the bar's width
  const ballWidthPercent = (horizontalMinigame.ballElement.offsetWidth / horizontalMinigame.barElement.offsetWidth) * 100;
  const ballCenterPercent = horizontalMinigame.ballPosition + ballWidthPercent / 2;

  const instructionElement = document.getElementById('minigame-instruction');


  if (ballCenterPercent >= horizontalMinigame.targetZoneStart && ballCenterPercent <= horizontalMinigame.targetZoneEnd) {
    horizontalMinigame.hitSuccess = true;
    // Don't end minigame immediately, let it play out or give feedback
    showNotification('Good hit!', 800, 'uncommon');
    // Potentially increase difficulty or change something for next round if multi-round
    // For now, one good hit is enough
    endHorizontalMinigame(true); 
  } else {
    horizontalMinigame.chances--;
     if (instructionElement) {
        instructionElement.textContent = `Press SPACE or TAP screen in the green zone! Chances: ${horizontalMinigame.chances}`;
    }
    showNotification(`Miss! Chances left: ${horizontalMinigame.chances}`, 1000, 'rare');
    if (horizontalMinigame.chances <= 0) {
      // Randomly choose between the two missed sound effects
      const soundNumber = Math.random() < 0.5 ? 1 : 2;
      const missedSound = document.getElementById(`missed-sound-${soundNumber}`);
      
      if (missedSound) {
        missedSound.currentTime = 0; // Reset to start of the sound
        missedSound.play().catch(error => console.log(`Error playing missed sound ${soundNumber}:`, error));
      }
      endHorizontalMinigame(false);
    }
    // Maybe reset ball position or give a slight pause
    horizontalMinigame.ballPosition = 0;
    horizontalMinigame.ballDirection = 1;
  }
}

function horizontalMinigameLoop() {
  if (!horizontalMinigame.isActive) return;
  
  // Get current time
  const currentTime = performance.now();
  
  // Calculate actual time elapsed since last frame in seconds
  const actualDelta = (currentTime - horizontalMinigame.lastTime) / 1000;
  
  // Update last time for next frame
  horizontalMinigame.lastTime = currentTime;
  
  // The fixed time step we want to use (60 FPS equivalent)
  const FIXED_STEP = 1/60;
  
  // Accumulate the actual time into our pool
  horizontalMinigame.timeAccumulator += actualDelta;
  
  // Define base speed in units per second
  const BASE_SPEED = 100; // 100% of the bar per second
  
  // Limit max accumulated time to prevent spiral of death if tab is inactive
  if (horizontalMinigame.timeAccumulator > 0.1) {
    horizontalMinigame.timeAccumulator = 0.1;
  }
  
  // Execute physics updates with fixed time steps
  // This ensures consistent speed regardless of frame rate
  let updatesPerformed = 0;
  while (horizontalMinigame.timeAccumulator >= FIXED_STEP && updatesPerformed < 3) {
    // Calculate movement for this fixed time step
    const frameIndependentSpeed = horizontalMinigame.ballSpeed * FIXED_STEP * BASE_SPEED;
    
    // Apply movement
    horizontalMinigame.ballPosition += frameIndependentSpeed * horizontalMinigame.ballDirection;
    
    // Handle boundaries
    const ballWidthPercent = (horizontalMinigame.ballElement.offsetWidth / horizontalMinigame.barElement.offsetWidth) * 100;
    if (horizontalMinigame.ballPosition >= (100 - ballWidthPercent)) {
      horizontalMinigame.ballPosition = (100 - ballWidthPercent);
      horizontalMinigame.ballDirection = -1;
    } else if (horizontalMinigame.ballPosition <= 0) {
      horizontalMinigame.ballPosition = 0;
      horizontalMinigame.ballDirection = 1;
    }
    
    // Consume the fixed time step from the accumulator
    horizontalMinigame.timeAccumulator -= FIXED_STEP;
    
    // Count updates to prevent spiral of death
    updatesPerformed++;
  }
  
  // Update UI with the new position
  updateHorizontalMinigameUI();
  
  // Request next frame
  horizontalMinigame.animationFrame = requestAnimationFrame(horizontalMinigameLoop);
}

function updateHorizontalMinigameUI() {
  if (horizontalMinigame.ballElement) {
    horizontalMinigame.ballElement.style.left = `${horizontalMinigame.ballPosition}%`;
  }
}
// --- End of New Minigame Implementation ---

// Get unlock requirement text for a pond
function getPondUnlockRequirement(pondIndex) {
  if (pondIndex >= 4 && pondIndex <= 5) {
    return 'Catch 20 fish to unlock!';
  } else if (pondIndex >= 6 && pondIndex <= 7) {
    return 'Discover 12 species to unlock!';
  }
  return '';
}

// Pond names for display
const POND_NAMES = [
  'Starter Pond',
  'Meadow Pond',
  'Garden Pond',
  'Rocky Pond',
  'Forest Pond',
  'Sunny Pond',
  'Hidden Pond',
  'Deep Pond'
];

// ===========================================
// STREAK DISPLAY SYSTEM
// ===========================================

/**
 * Get streak bonus multiplier based on current streak
 */
function getStreakBonus(streak) {
  if (streak >= 10) return { multiplier: 2.0, text: '+100%' };
  if (streak >= 7) return { multiplier: 1.75, text: '+75%' };
  if (streak >= 5) return { multiplier: 1.5, text: '+50%' };
  if (streak >= 3) return { multiplier: 1.25, text: '+25%' };
  return { multiplier: 1.0, text: '' };
}

/**
 * Update the streak display UI
 */
function updateStreakDisplay(streak, animate = false) {
  const display = document.getElementById('streak-display');
  const countEl = document.getElementById('streak-count');
  const bonusEl = document.getElementById('streak-bonus');
  const flames = document.querySelectorAll('#streak-flames .flame');

  if (!display || !countEl) return;

  // Update count
  countEl.textContent = streak;

  // Update bonus text
  const bonus = getStreakBonus(streak);
  if (bonusEl) {
    bonusEl.textContent = bonus.text;
  }

  // Update flame activation (1 flame per 2 streak, max 3)
  const activeFlames = Math.min(3, Math.ceil(streak / 2));
  flames.forEach((flame, index) => {
    if (index < activeFlames) {
      flame.classList.add('active');
    } else {
      flame.classList.remove('active');
    }
  });

  // Update intensity level
  display.classList.remove('intensity-1', 'intensity-2', 'intensity-3');
  if (streak >= 10) {
    display.classList.add('intensity-3');
  } else if (streak >= 5) {
    display.classList.add('intensity-2');
  } else if (streak >= 1) {
    display.classList.add('intensity-1');
  }

  // Show/hide display based on streak
  if (streak > 0) {
    display.classList.remove('hidden');
    display.classList.add('visible');

    // Pulse animation on increase
    if (animate) {
      display.classList.remove('streak-up');
      void display.offsetWidth; // Trigger reflow
      display.classList.add('streak-up');
    }
  } else {
    display.classList.remove('visible');
    display.classList.add('hidden');
  }
}

/**
 * Show streak break animation and notification
 */
function showStreakBreak(previousStreak) {
  if (previousStreak < 3) return; // Only show for meaningful streaks

  const display = document.getElementById('streak-display');
  if (display && display.classList.contains('visible')) {
    display.classList.add('breaking');

    // Reset after animation
    setTimeout(() => {
      display.classList.remove('breaking', 'visible');
      display.classList.add('hidden');
      updateStreakDisplay(0);
    }, 600);
  }

  // Show notification about lost streak
  showNotification(`Streak lost! (${previousStreak} catches)`, 2000, 'common');
}

// Show pond tooltip with info
function showPondTooltip(fishingHole) {
  const tooltip = document.getElementById('pond-tooltip');
  if (!tooltip) return;

  const pondIndex = fishingHole.index;
  const isUnlocked = progressionSystem.isPondUnlocked(pondIndex);
  const tier = fishingHole.tier || (pondIndex <= 3 ? 'tier1' : (pondIndex <= 5 ? 'tier2' : 'tier3'));
  const tierNames = { tier1: 'Starter', tier2: 'Advanced', tier3: 'Expert' };

  // Update title
  document.getElementById('pond-tooltip-title').textContent = POND_NAMES[pondIndex] || `Pond ${pondIndex + 1}`;

  // Update status
  const statusIcon = document.getElementById('pond-status-icon');
  const statusText = document.getElementById('pond-status-text');
  statusIcon.textContent = isUnlocked ? '🔓' : '🔒';
  statusText.textContent = isUnlocked ? 'Unlocked' : 'Locked';
  statusText.className = isUnlocked ? 'unlocked' : 'locked';

  // Update tier badge
  const tierBadge = document.getElementById('pond-tier-badge');
  tierBadge.textContent = tierNames[tier];
  tierBadge.className = `tier-badge ${tier}`;

  // Update fish list
  const fishList = document.getElementById('pond-fish-list');
  fishList.innerHTML = '';

  const pondPool = progressionSystem.getFishPoolForPond(pondIndex, FISH_TYPES);
  if (pondPool && pondPool.fish) {
    pondPool.fish.forEach(entry => {
      if (entry.fish) {
        const fishItem = document.createElement('div');
        fishItem.className = 'pond-fish-item';

        // Check if player has discovered this fish
        const discovered = gameState.inventory[entry.fish.id]?.discovered;
        const fishName = discovered ? entry.fish.name : '???';
        const fishIcon = discovered ? '🐟' : '❓';

        fishItem.innerHTML = `
          <span class="pond-fish-icon">${fishIcon}</span>
          <span class="pond-fish-name">${fishName}</span>
          <span class="pond-fish-rarity ${entry.fish.rarity}">${entry.fish.rarity}</span>
        `;
        fishList.appendChild(fishItem);
      }
    });
  }

  // Update unlock requirement
  const unlockSection = document.getElementById('pond-tooltip-unlock');
  const unlockText = document.getElementById('unlock-text');
  if (!isUnlocked) {
    unlockSection.style.display = 'block';
    unlockText.textContent = getPondUnlockRequirement(pondIndex);
  } else {
    unlockSection.style.display = 'none';
  }

  // Show tooltip
  tooltip.style.display = 'flex';
}

// Close pond tooltip
function closePondTooltip() {
  const tooltip = document.getElementById('pond-tooltip');
  if (tooltip) {
    tooltip.style.display = 'none';
  }
}

// Set up pond tooltip event listeners
function setupPondTooltipListeners() {
  const closeBtn = document.getElementById('pond-tooltip-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', closePondTooltip);
  }

  // Close when clicking outside
  const tooltip = document.getElementById('pond-tooltip');
  if (tooltip) {
    tooltip.addEventListener('click', (e) => {
      if (e.target === tooltip) {
        closePondTooltip();
      }
    });
  }

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closePondTooltip();
    }
  });
}

// Handle fishing mechanics
function startFishing(fishingHole) {
  if (gameState.isFishing || gameState.isTransitioning) return;

  // Ocean fishing is always allowed, pond fishing requires unlock check
  if (!fishingHole.isOcean) {
    // Check if pond is unlocked - show tooltip for locked ponds
    if (!progressionSystem.isPondUnlocked(fishingHole.index)) {
      showPondTooltip(fishingHole);
      return;
    }
  }

  let fishingPosition;

  if (fishingHole.isOcean) {
    // For ocean fishing, move player to the beach edge (on the sand) facing the fishing spot
    const dirToOcean = new THREE.Vector3().subVectors(fishingHole.position, new THREE.Vector3(0, 0, 0)).normalize();
    // Position player on the beach (at outer edge of sand, near water)
    fishingPosition = dirToOcean.multiplyScalar(groundRadius + 12);
    fishingPosition.y = 0;
  } else {
    // For pond fishing, position near the pond
    const direction = new THREE.Vector3().subVectors(fishingHole.position, player.position).normalize();
    const distanceFromHole = 4.5; // Increased from 3 to stand on sand
    fishingPosition = new THREE.Vector3().copy(fishingHole.position).sub(direction.multiplyScalar(distanceFromHole));
    // Set height based on terrain so player stands on ground
    fishingPosition.y = getTerrainHeightAt(fishingPosition.x, fishingPosition.z) + 1;
  }

  movePlayerToPosition(fishingPosition);

  const checkPositionInterval = setInterval(() => {
    if (!gameState.playerMoving) {
      clearInterval(checkPositionInterval);

      // Play the throwing sound effect first
      const throwingSound = document.getElementById('throwing-sound');
      if (throwingSound) {
        throwingSound.currentTime = 0; // Reset to start of the sound
        throwingSound.play().catch(error => console.log("Error playing throwing sound:", error));
      }
      
      // Short delay to let the sound start before the visual
      setTimeout(() => {
        gameState.isFishing = true;
        gameState.activeFishingHole = fishingHole;
        createFishingRod(player.position);
      }, 100); // 100ms delay for the sound to start playing

      const biteTime = 2000 + Math.random() * 3000; // 2-5 seconds, slightly shorter wait
      gameState.fishingTimer = setTimeout(() => {
        if (gameState.isFishing) {
          // Determine the fish that will be caught based on water type
          let caughtFishType;
          if (fishingHole.isOcean) {
            // Ocean fishing - saltwater fish
            caughtFishType = getRandomFish('saltwater');
          } else {
            // Pond fishing - freshwater fish from progression system
            const pondFish = progressionSystem.getRandomFishFromPond(fishingHole.index, FISH_TYPES);
            caughtFishType = pondFish || getRandomFish('freshwater');
          }
          gameState.potentialFish = caughtFishType;
          const fishRarity = gameState.potentialFish.rarity;
          
          gameState.fishBiting = true; // This flag is useful for visual cues like bobbing
          animateFishBite();
          
          // Start mini-game with difficulty based on fish rarity
          startHorizontalMinigame(fishRarity);
        }
      }, biteTime);
    }
  }, 100);
}

function animateFishBite() {
  if (!gameState.fishingFloat) return;
  
  const bobAnimation = () => {
    if (!gameState.fishBiting || !gameState.fishingFloat || horizontalMinigame.isActive) return; // Stop bobbing if minigame started
    
    gameState.fishingFloat.position.y += (Math.random() - 0.5) * 0.2;
    setTimeout(bobAnimation, 100);
  };
  
  bobAnimation();
}

function catchFish() {
  // This function is called by endHorizontalMinigame(true)
  if (!gameState.activeFishingHole) {
      console.warn("catchFish called without active fishing hole");
      endFishing(false); // Safety exit
      return;
  }
  
  // Play the caught sound effect
  const caughtSound = document.getElementById('caught-sound');
  if (caughtSound) {
    // Clone the audio node for reliable playback of overlapping sounds
    const caughtSoundClone = caughtSound.cloneNode(true);
    caughtSoundClone.volume = caughtSound.volume; // Maintain volume settings
    caughtSoundClone.play().catch(error => console.log("Error playing caught sound:", error));
    
    // Cleanup to prevent memory issues
    caughtSoundClone.onended = function() {
      caughtSoundClone.remove(); // Remove clone after it's done playing
    };
  }
  
  // gameState.fishBiting = false; // Already handled by endHorizontalMinigame or endFishing

  // Use the fish that was determined when the mini-game started
  const caughtFish = gameState.potentialFish;
  gameState.caughtFish = caughtFish;

  // Increment streak
  gameState.consecutiveCatches = (gameState.consecutiveCatches || 0) + 1;
  const currentStreak = gameState.consecutiveCatches;

  // Calculate bonus points from streak
  const streakBonus = getStreakBonus(currentStreak);
  const basePoints = caughtFish.points;
  const bonusPoints = Math.floor(basePoints * (streakBonus.multiplier - 1));
  const totalPoints = basePoints + bonusPoints;

  gameState.fishCaught++;
  gameState.score += totalPoints;
  document.getElementById('fish-count').textContent = `Fish Caught: ${gameState.fishCaught} | Score: ${gameState.score}`;

  // Update streak display
  updateStreakDisplay(currentStreak, true);
  
  const fishId = caughtFish.id; // Use the predefined ID from FISH_TYPES
  
  let isFirstCatch = false;
  if (gameState.inventory[fishId] && gameState.inventory[fishId].discovered) {
    gameState.inventory[fishId].count++;
  } else {
    isFirstCatch = true;
    gameState.inventory[fishId] = {
      fish: caughtFish,
      count: 1,
      discovered: true,
      firstCaught: new Date().toLocaleDateString()
    };
  }

  // Emit fish caught event for achievement/progression systems
  eventBus.emit(GameEvents.FISH_CAUGHT, {
    fish: caughtFish,
    isFirstCatch,
    totalCaught: gameState.fishCaught,
    pondIndex: gameState.activeFishingHole?.index
  });

  if (gameState.showInventory) {
    updateInventoryUI();
  }

  if(gameState.fishingFloat) createFishSplash(scene, gameState.fishingFloat.position);

  // Show 3D fish preview modal with streak bonus info
  const bonusInfo = bonusPoints > 0 ? { bonusPoints, streakCount: currentStreak } : null;
  showFishPreview(caughtFish, isFirstCatch, () => {
    // Called when preview is closed
    endFishing(true);
  }, bonusInfo);

  // Also show the caught fish animation in the world
  showCaughtFish(caughtFish);
}

// Modify showNotification to accept fishData for sprite display
function showNotification(message, duration = 2000, rarity = 'common', fishData = null) {
  const container = document.getElementById('notification-container');
  const notification = document.createElement('div');
  notification.className = `notification ${rarity.toLowerCase()}`;
  notification.style.display = 'flex'; // Use flex to align sprite and text
  notification.style.alignItems = 'center';

  let icon = '';
  switch(rarity.toLowerCase()) {
    case 'legendary': icon = '🌟 '; break;
    case 'rare':      icon = '✨ '; break;
    case 'uncommon':  icon = '⭐ '; break;
    // default:          icon = '🐟 '; // Removing default fish icon if we show sprite
  }

  // If fishData is provided, create and add the sprite
  if (fishData && fishData.image_url) {
    const fishSpriteDiv = document.createElement('div');
    fishSpriteDiv.style.width = '32px'; // Sprite size in notification
    fishSpriteDiv.style.height = '32px';
    fishSpriteDiv.style.backgroundImage = `url(${fishData.image_url})`;
    fishSpriteDiv.style.backgroundSize = '300% 300%'; // 3x3 grid
    const xPos = fishData.sprite_col * 50;
    const yPos = fishData.sprite_row * 50;
    fishSpriteDiv.style.backgroundPosition = `${xPos}% ${yPos}%`;
    // fishSpriteDiv.style.imageRendering = 'pixelated'; // Removed for smoother scaling
    fishSpriteDiv.style.marginRight = '10px'; // Space between sprite and text
    fishSpriteDiv.style.flexShrink = '0'; // Prevent sprite from shrinking
    notification.appendChild(fishSpriteDiv);
  } else if (icon) { // If no fish sprite, but there is an icon (e.g. for rarity)
    const iconSpan = document.createElement('span');
    iconSpan.textContent = icon;
    iconSpan.style.marginRight = '5px';
    notification.appendChild(iconSpan);
  }

  const messageSpan = document.createElement('span');
  messageSpan.textContent = message;
  notification.appendChild(messageSpan);
  
  container.appendChild(notification);

  setTimeout(() => {
    notification.style.animation = 'fadeOut 0.5s ease-in forwards';
    setTimeout(() => {
      if (container.contains(notification)) container.removeChild(notification);
    }, 500);
  }, duration);
}

function showCaughtFish(fish) {
  const fishModel = fish.model.clone();
  if(gameState.fishingFloat) fishModel.position.copy(gameState.fishingFloat.position);
  else fishModel.position.set(player.position.x, 1, player.position.z + 2); // Fallback position
  fishModel.position.y += 1;
  scene.add(fishModel);
  const fishInfoElement = document.createElement('div');
  fishInfoElement.style.position = 'absolute';
  fishInfoElement.style.top = '50%';
  fishInfoElement.style.left = '50%';
  fishInfoElement.style.transform = 'translate(-50%, -50%)';
  fishInfoElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
  fishInfoElement.style.color = 'white';
  fishInfoElement.style.padding = '20px';
  fishInfoElement.style.borderRadius = '10px';
  fishInfoElement.style.textAlign = 'center';
  fishInfoElement.style.zIndex = '1000';
  fishInfoElement.style.opacity = '1';
  fishInfoElement.style.transition = 'opacity 0.5s ease-out';
  let rarityColor;
  switch (fish.rarity) {
    case 'legendary': rarityColor = '#9c27b0'; break;
    case 'rare':      rarityColor = '#ffc107'; break;
    case 'uncommon':  rarityColor = '#4caf50'; break;
    default:          rarityColor = '#2196f3';
  }
  fishInfoElement.innerHTML = `
    <h2 style="margin: 0 0 10px 0;">You caught a <span style="color: ${rarityColor}">${fish.name}</span>!</h2>
    <p>Rarity: <span style="color: ${rarityColor}">${fish.rarity.toUpperCase()}</span></p>
    <p>Points: ${fish.points}</p>
  `;
  document.body.appendChild(fishInfoElement);
  const startTime = Date.now();
  let animationFrameId = null;
  const animateFish = () => {
    const elapsed = Date.now() - startTime;
    if(gameState.fishingFloat) fishModel.position.y = gameState.fishingFloat.position.y + 1 + Math.sin(elapsed / 200) * 0.5;
    else fishModel.position.y = 1 + Math.sin(elapsed / 200) * 0.5; // Fallback y animation
    fishModel.rotation.y = elapsed / 500;
    fishModel.rotation.z = Math.sin(elapsed / 300) * 0.2;
    if (elapsed < 2000) {
      animationFrameId = requestAnimationFrame(animateFish);
    } else {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if(scene.children.includes(fishModel)) scene.remove(fishModel);
    }
  };
  animationFrameId = requestAnimationFrame(animateFish);
  setTimeout(() => { fishInfoElement.style.opacity = '0'; }, 2000);
  setTimeout(() => {
    if (document.body.contains(fishInfoElement)) document.body.removeChild(fishInfoElement);
  }, 2500);
}

function endFishing(success) {
  clearTimeout(gameState.fishingTimer); // Clear the fish bite timer
  gameState.isFishing = false;
  gameState.fishBiting = false; // Reset this flag important for animateFishBite

  // Ensure minigame is properly ended if it was active
  if (horizontalMinigame.isActive) {
     // If endFishing is called due to failure, success would be false.
     // If called after catchFish, success would be true.
     // We don't want to call endHorizontalMinigame again if it led here.
     // So, just ensure its state is reset.
    horizontalMinigame.isActive = false; 
    if (horizontalMinigame.container) horizontalMinigame.container.style.display = 'none';
    window.removeEventListener('keydown', horizontalMinigameKeyDown);
    if (horizontalMinigame.animationFrame) cancelAnimationFrame(horizontalMinigame.animationFrame);
  }

  if (gameState.fishingRod) {
    scene.remove(gameState.fishingRod);
    scene.remove(gameState.fishingLine);
    scene.remove(gameState.fishingFloat);
    gameState.fishingRod = null;
    gameState.fishingLine = null;
    gameState.fishingFloat = null;
  }

  if (!success && gameState.activeFishingHole) { // Only show "got away" if fishing actually failed
      // Break the streak
      const previousStreak = gameState.consecutiveCatches || 0;
      if (previousStreak > 0) {
        showStreakBreak(previousStreak);
        gameState.consecutiveCatches = 0;
      }
  }
  gameState.activeFishingHole = null; 
}

// Raycasting for interaction
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
let lastTouch = null; // Track last touch position for mobile

// Touch event handler for mobile devices
function onTouchStart(event) {
  // Don't process touches during transitions
  if (gameState.isTransitioning) return;
  
  // Check if the touch occurred on a UI panel
  const uiElements = [
    'horizontal-minigame-container',
    'inventory-panel',
    'fish-detail',
    'welcome-screen',
    'audio-controls',
    'notification-container',
    'transition-message',
    'achievements-panel',
    'settings-panel',
    'stats-modal',
    'reset-confirm-modal',
    'pond-tooltip',
    'fish-preview-modal',
    'streak-display',
    'progression-hud',
    'settings-button',
    'achievements-button'
  ];

  // Check if the touch target is or is contained within any UI element
  let isUITouch = false;
  for (const elementId of uiElements) {
    const element = document.getElementById(elementId);
    if (element && (event.target === element || element.contains(event.target))) {
      isUITouch = true;
      break;
    }
  }
  
  // If the touch was on a UI element, don't process it for game world interaction
  if (isUITouch) return;
  
  // Store the touch for later movement tracking
  lastTouch = { 
    x: event.touches[0].clientX, 
    y: event.touches[0].clientY 
  };
  
  // Get canvas position for accurate coordinate calculation
  const canvas = renderer.domElement;
  const rect = canvas.getBoundingClientRect();
  
  // Calculate touch position in normalized device coordinates
  // using the canvas position and dimensions instead of window
  mouse.x = ((event.touches[0].clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.touches[0].clientY - rect.top) / rect.height) * 2 + 1;
  
  // Handle the interaction using the shared logic
  handleInteraction();
}

// Handle touch move events - prevent unwanted scrolling in game while allowing scrolling in UI panels
function onTouchMove(event) {
  // Only process if the game has started
  if (gameStarted) {
    // Check if we're touching the inventory panel or fish detail view
    const inventoryPanel = document.getElementById('inventory-panel');
    const fishDetail = document.getElementById('fish-detail');
    const inventoryContent = document.getElementById('inventory-content');
    const detailContent = document.getElementById('detail-content');
    
    // Allow natural scrolling in these UI elements
    let isScrollableElement = false;
    const target = event.target;
    
    // Check if the touch is within one of our scrollable containers
    if (inventoryContent && (target === inventoryContent || inventoryContent.contains(target))) {
      isScrollableElement = true;
    }
    
    if (detailContent && (target === detailContent || detailContent.contains(target))) {
      isScrollableElement = true;
    }
    
    // If we're not touching a scrollable element, prevent default to avoid unwanted page scrolling
    if (!isScrollableElement) {
      event.preventDefault();
    }
  }
}

// Added to handle touch end events to complete the touch interaction system
function onTouchEnd(event) {
  // No specific behavior needed for now, but included for completeness
}

// Shared interaction logic for both mouse clicks and touch events
function handleInteraction() {
  // Update the picking ray with the camera and mouse position
  raycaster.setFromCamera(mouse, camera);
  
  if (!gameState.isInHouse) {
    // EXTERIOR SCENE INTERACTIONS

    // Check for intersections with pond fishing holes (freshwater)
    const waterObjects = fishingHoles.map(hole => hole.water);
    const intersects = raycaster.intersectObjects(waterObjects);

    if (intersects.length > 0) {
      const clickedWater = intersects[0].object;
      const fishingHole = fishingHoles.find(hole => hole.water === clickedWater);

      if (fishingHole) {
        // Shift+click to view pond info without fishing
        if (window.isShiftHeld) {
          showPondTooltip(fishingHole);
        } else {
          startFishing(fishingHole);
        }
        return;
      }
    }

    // Check if we clicked on the house door
    const doorIntersects = raycaster.intersectObject(gameState.house.door);
    if (doorIntersects.length > 0) {
      // Move to the door first
      const doorPosition = new THREE.Vector3().copy(gameState.house.position);
      doorPosition.z += 2; // Door is at the front
      movePlayerToPosition(doorPosition);
      return;
    }

    // Check what was clicked - ground or ocean
    // First check ground only
    const groundIntersects = raycaster.intersectObject(ground);
    if (groundIntersects.length > 0) {
      const clickPoint = groundIntersects[0].point;

      // Check if click is within visible terrain (not the transparent edge)
      const distFromCenter = Math.sqrt(clickPoint.x * clickPoint.x + clickPoint.z * clickPoint.z);
      const visibleTerrainRadius = groundRadius - 5; // Visible terrain ends before full radius due to edge fade

      if (distFromCenter > visibleTerrainRadius) {
        // Click is on invisible part of terrain mesh - treat as ocean click
        // Calculate direction from center to click point
        const dirToOcean = new THREE.Vector3(clickPoint.x, 0, clickPoint.z).normalize();

        // Position player at beach edge (just inside visible terrain)
        const beachPosition = dirToOcean.clone().multiplyScalar(visibleTerrainRadius - 2);
        beachPosition.y = getTerrainHeightAt(beachPosition.x, beachPosition.z) + 1;

        // Create ocean fishing hole at the click point
        const oceanHole = {
          position: new THREE.Vector3(clickPoint.x, -2.0, clickPoint.z),
          isOcean: true
        };

        // Move player to beach, then start fishing when they arrive
        gameState.pendingOceanFishing = oceanHole;
        movePlayerToPosition(beachPosition);
        return;
      }

      let targetHole = null;

      // Check if clicked point is physically inside any pond radius (approx 3.0 units)
      // This catches cases where the user clicks the pond bed instead of the water surface
      for (const hole of fishingHoles) {
        // Calculate horizontal distance (ignore Y difference)
        const dx = clickPoint.x - hole.position.x;
        const dz = clickPoint.z - hole.position.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        // Use 2.8 as a safe radius (water visual radius is 3.0)
        // This ensures we catch clicks clearly inside the water
        if (dist < 2.8) {
          targetHole = hole;
          break;
        }
      }

      if (targetHole) {
         if (window.isShiftHeld) {
            showPondTooltip(targetHole);
         } else {
            startFishing(targetHole);
         }
         return;
      }

      // Clicked on valid terrain - walk there
      const targetPosition = clickPoint.clone();
      movePlayerToPosition(targetPosition);
      return;
    }

    // If not ground, check ocean (for clicks that miss terrain entirely)
    if (window.gameOcean) {
      const oceanIntersects = raycaster.intersectObject(window.gameOcean);
      if (oceanIntersects.length > 0) {
        // Clicked on ocean - move to beach edge and fish from there
        const clickPoint = oceanIntersects[0].point;

        // Calculate direction from center to click point
        const dirToOcean = new THREE.Vector3(clickPoint.x, 0, clickPoint.z).normalize();

        // Position player at beach edge
        const visibleTerrainRadius = groundRadius - 5;
        const beachPosition = dirToOcean.clone().multiplyScalar(visibleTerrainRadius - 2);
        beachPosition.y = getTerrainHeightAt(beachPosition.x, beachPosition.z) + 1;

        // Create ocean fishing hole
        const oceanHole = {
          position: new THREE.Vector3(clickPoint.x, -2.0, clickPoint.z),
          isOcean: true
        };

        // Move player to beach, then start fishing when they arrive
        gameState.pendingOceanFishing = oceanHole;
        movePlayerToPosition(beachPosition);
        return;
      }
    }
  } else {
    // INTERIOR SCENE INTERACTIONS
    
    // Check if we clicked on the floor inside the house
    const floorIntersects = raycaster.intersectObject(gameState.houseInterior.children[0]); // Floor is the first child
    
    if (floorIntersects.length > 0) {
      const targetPosition = floorIntersects[0].point;
      movePlayerToPosition(targetPosition);
    }
  }
}

function onMouseClick(event) {
  // Don't process clicks during transitions
  if (gameState.isTransitioning) return;
  
  // Check if the click occurred on a UI panel
  const uiElements = [
    'horizontal-minigame-container',
    'inventory-panel',
    'fish-detail',
    'welcome-screen',
    'audio-controls',
    'notification-container',
    'transition-message',
    'achievements-panel',
    'settings-panel',
    'stats-modal',
    'reset-confirm-modal',
    'pond-tooltip',
    'fish-preview-modal',
    'streak-display',
    'progression-hud',
    'settings-button',
    'achievements-button'
  ];

  // Check if the click target is or is contained within any UI element
  let isUIClick = false;
  for (const elementId of uiElements) {
    const element = document.getElementById(elementId);
    if (element && (event.target === element || element.contains(event.target))) {
      isUIClick = true;
      break;
    }
  }
  
  // If the click was on a UI element, don't process it for game world interaction
  if (isUIClick) return;
  
  // Get canvas position for accurate coordinate calculation
  const canvas = renderer.domElement;
  const rect = canvas.getBoundingClientRect();
  
  // Calculate mouse position in normalized device coordinates
  // using the canvas position and dimensions instead of window
  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  
  // Handle the interaction using the shared logic
  handleInteraction();
}

// Handle keyboard input
function onKeyDown(event) {
  // Don't process keystrokes during transitions
  if (gameState.isTransitioning) return;
  
  // New minigame handles its own space bar via horizontalMinigameKeyDown
  // So, no fishing-related space bar check here.
  
  // Inventory toggle with 'I' key
  if (event.key === 'i' || event.key === 'I') {
    toggleInventory();
  }

  // Toggle FPS counter with 'F' key
  if (event.key === 'f' || event.key === 'F') {
    toggleStats();
  }

  // Toggle weather with 'C' key
  if (event.key === 'c' || event.key === 'C') {
    toggleWeather();
  }
  
  // Toggle ground leaves with 'L' key
  if (event.key === 'l' || event.key === 'L') {
    toggleGroundLeaves();
  }

  // Horizontal Minigame Controls
  if (horizontalMinigame.isActive) {
  }
  
  // Handle Escape key for multiple functions
  if (event.key === 'Escape') {
    if (gameState.showInventory) {
      toggleInventory(); // Close inventory first if open
    } else if (horizontalMinigame.isActive) { 
      endHorizontalMinigame(false); // Close minigame if active
    } else if (gameState.isInHouse && !gameState.isTransitioning) {
      exitHouse(); // Then exit house
    }
  }
  
  // Toggle FPS counter with '0' key
  if (event.key === '0') {
    toggleStats();
  }
}

// Function to toggle ground leaves visibility
function toggleGroundLeaves() {
  gameConfig.showGroundLeaves = !gameConfig.showGroundLeaves;
  
  // Check if we've already created ground leaves
  if (window.groundLeaves && window.groundLeaves.length > 0) {
    window.groundLeaves.forEach(leaf => {
      leaf.visible = gameConfig.showGroundLeaves;
    });
    
    showNotification(
      gameConfig.showGroundLeaves ? 'Ground leaves visible' : 'Ground leaves hidden', 
      2000
    );
    
    console.log(`Ground leaves ${gameConfig.showGroundLeaves ? 'visible' : 'hidden'}, count: ${window.groundLeaves.length}`);
  } else {
    console.log('No ground leaves to toggle');
    showNotification('No ground leaves to show/hide', 2000);
  }
}

// Function to toggle weather state
function toggleWeather() {
  if (gameState.weather === 'sunny') {
    gameState.weather = 'cloudy';
    if (ground && ground.material && ground.material.uniforms.cloudShadowStrength) {
      ground.material.uniforms.cloudShadowStrength.value = 0.45; // More visible shadows
      // Randomize starting position of clouds
      ground.material.uniforms.cloudShadowOffset.value.x = Math.random();
      ground.material.uniforms.cloudShadowOffset.value.y = Math.random();
      console.log("Weather changed to Cloudy. Subtle shadows ON.");
    }
    // Make sky clouds visible
    skyClouds.forEach(cloud => cloud.visible = true);
    showNotification('Clouds visible', 1500);
  } else {
    gameState.weather = 'sunny';
    if (ground && ground.material && ground.material.uniforms.cloudShadowStrength) {
      ground.material.uniforms.cloudShadowStrength.value = 0.0; // No shadows for sunny
      console.log("Weather changed to Sunny. Shadows OFF.");
    }
    // Hide sky clouds
    skyClouds.forEach(cloud => cloud.visible = false);
    showNotification('Clouds hidden', 1500);
  }
}

// Handle window resize
function onWindowResize() {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);

  // Update post-processing composer (if initialized)
  if (composer && bloomPass) {
    const pixelRatio = renderer.getPixelRatio();
    composer.setSize(window.innerWidth, window.innerHeight);
    composer.setPixelRatio(pixelRatio);
    bloomPass.resolution.set(window.innerWidth, window.innerHeight);
  }
}

// Stats already initialized at the top of the file
// Just add the DOM element to the body
document.body.appendChild(stats.dom);

// Position the stats panel at the bottom right
stats.dom.style.position = 'absolute';
stats.dom.style.right = '10px';
stats.dom.style.left = 'auto';
stats.dom.style.top = 'auto';
stats.dom.style.bottom = '10px'; // Position at the bottom
stats.dom.style.zIndex = '1000'; // Make sure it's visible above other elements

// Variable to track FPS counter visibility
let statsVisible = true;

// Function to toggle FPS counter visibility
function toggleStats() {
  statsVisible = !statsVisible;
  stats.dom.style.display = statsVisible ? 'block' : 'none';
}

// Clock already initialized at the top of the file

// Event listeners
window.addEventListener('click', onMouseClick);

// Touch event listeners with appropriate passive settings
window.addEventListener('touchstart', onTouchStart); // Using default passive: true for better performance
window.addEventListener('touchmove', onTouchMove, { passive: false }); // Need non-passive to prevent scrolling
window.addEventListener('touchend', onTouchEnd); // Using default passive: true for better performance

window.addEventListener('keydown', onKeyDown);
window.addEventListener('resize', onWindowResize);

// Track Shift key for pond info tooltip (Shift+click on pond)
window.isShiftHeld = false;
window.addEventListener('keydown', (e) => {
  if (e.key === 'Shift') window.isShiftHeld = true;
});
window.addEventListener('keyup', (e) => {
  if (e.key === 'Shift') window.isShiftHeld = false;
});

// Animation loop with fixed time step for consistent movement across all devices
function animate() {
  // Only run animation if game has started
  if (!gameStarted) return;
  
  requestAnimationFrame(animate);

  // Get actual time since last frame
  const actualDeltaTime = clock.getDelta();
  
  // Get total elapsed time (for continuous animations like water)
  const time = clock.getElapsedTime();
  
  // Fixed time step approach
  // This is the key to true frame rate independence
  const FIXED_DELTA = 1/60; // Target: 60 FPS equivalent
  
  // Initialize timePool if it doesn't exist
  if (gameState.timePool === undefined) {
    gameState.timePool = 0;
  }
  
  // Accumulate actual time into our pool
  gameState.timePool += actualDeltaTime;
  
  // Execute 0, 1, or multiple updates depending on accumulated time
  // This ensures the same number of physics/movement updates regardless of frame rate
  let stepsTaken = 0;
  
  while (gameState.timePool >= FIXED_DELTA) {
    // Pass fixed delta to all game logic
    window.currentDeltaTime = FIXED_DELTA;
    
    // Run movement and game logic updates with fixed time step
    updatePlayerMovement();
    
    // Remove the used time from the pool
    gameState.timePool -= FIXED_DELTA;
    
    // Safety valve - prevent spiral of death if frame rate drops too low
    stepsTaken++;
    if (stepsTaken > 3) {
      gameState.timePool = 0;
      break;
    }
  }
  
  // For rendering code that needs the actual deltaTime (not the fixed step)
  window.actualDeltaTime = actualDeltaTime;
  window.currentElapsedTime = time;

  // Begin FPS measurement
  stats.begin();

  // Player movement is now handled in the fixed time step loop above
  // Only visual/rendering updates happen here

  // Update player animation mixer
  if (playerMixer) {
    playerMixer.update(actualDeltaTime);
  }

  // Update our Wind Waker style water shader - frame-rate independent version
  fishingHoles.forEach(hole => {
    if (hole.water && hole.water.material && hole.water.material.uniforms && hole.water.material.uniforms.time) {
      // Always use the same clock time value for consistent animations
      // Using elapsed time (not delta) so animation is smooth and continuous

      // Use hole.index to create variations in animation speed, but overall slower for small ponds
      const speedVariation = 0.3 + ((hole.index || 0) % 3) * 0.1; // Values from 0.3 to 0.5

      // Apply time to the water shader with variation
      // Using our globally synchronized time value to ensure consistency across devices
      hole.water.material.uniforms.time.value = window.currentElapsedTime * speedVariation;
    }

    // Update shimmer effect for rare ponds
    if (hole.shimmerMesh && hole.shimmerMesh.visible && hole.shimmerMesh.material.uniforms) {
      hole.shimmerMesh.material.uniforms.time.value = window.currentElapsedTime;
    }
  });

  // Animate ocean water
  if (window.gameOcean && window.gameOcean.material.uniforms && window.gameOcean.material.uniforms.time) {
    window.gameOcean.material.uniforms.time.value = time;
  }

  // Foam is now integrated into ocean shader - no separate update needed

  // Update fish shadows swimming in ponds
  updateFishShadows(actualDeltaTime);

  // Update camera to follow player (Animal Crossing style)
  updateCameraFollow();
  
  // Update cloud shadow offset for animation - frame-rate independent version
  if (ground && ground.material && ground.material.uniforms.cloudShadowOffset) {
    // Use elapsed time from the clock instead of Date.now()
    // This is more consistent and frame-rate independent
    
    // Apply animation using simple time-based patterns
    ground.material.uniforms.cloudShadowOffset.value.x = fract(time * 0.025);
    ground.material.uniforms.cloudShadowOffset.value.y = fract(time * 0.015);
  }

  // --- Animate Sky Clouds - frame-rate independent version ---
  const cloudAreaHalf = cloudAreaSize / 2;
  skyClouds.forEach((cloud, index) => {
    if (cloud.visible) { 
      // Use elapsed time from the clock instead of Date.now()
      // This ensures consistent speed regardless of frame rate
      
      // Apply simple time-based patterns that match shadow movement
      const shadowUvX = fract(time * 0.01);
      const shadowUvZ = fract(time * 0.006);
      
      // Convert from UV 0-1 space to world space, with offset to keep initial positions
      const cloudBaseX = (shadowUvX - 0.5) * CLOUD_WORLD_MOVEMENT_SCALE;
      const cloudBaseZ = (shadowUvZ - 0.5) * CLOUD_WORLD_MOVEMENT_SCALE;
      
      // Each cloud has a fixed offset from this base position, giving them an initial spread
      if (!cloud.initialOffset) {
        // The first time this runs, save the cloud's starting position relative to the base
        cloud.initialOffset = {
          x: cloud.position.x - cloudBaseX,
          z: cloud.position.z - cloudBaseZ
        };
      }
      
      // Apply motion: base position (derived from frame-rate independent values) + initial offset
      cloud.position.x = cloudBaseX + cloud.initialOffset.x;
      cloud.position.z = cloudBaseZ + cloud.initialOffset.z;
      
      // Wrap clouds that go too far off-map back to the other side
      // This preserves their relative spacing and doesn't disturb the cloud pattern
      if (cloud.position.x > cloudAreaHalf + cloud.scale.x) {
        cloud.initialOffset.x -= cloudAreaSize;
      } else if (cloud.position.x < -cloudAreaHalf - cloud.scale.x) {
        cloud.initialOffset.x += cloudAreaSize;
      }
      
      if (cloud.position.z > cloudAreaHalf + cloud.scale.x) {
        cloud.initialOffset.z -= cloudAreaSize;
      } else if (cloud.position.z < -cloudAreaHalf - cloud.scale.x) {
        cloud.initialOffset.z += cloudAreaSize;
      }
    }
  });

  // Debug: log render path once
  if (!window._renderPathLogged) {
    console.log('Render path check:', {
      hasComposer: !!composer,
      hasRenderPass: !!renderPass,
      isTransitioning: gameState.isTransitioning,
      isInHouse: gameState.isInHouse
    });
    window._renderPathLogged = true;
  }

  // Render the correct scene based on player location
  if (gameState.isTransitioning) {
    // If transitioning, render through the iris effect shader
    if (gameState.irisEffect && gameState.irisEffect.material) {
      renderer.setRenderTarget(null); // Ensure rendering to screen
      renderer.render(gameState.irisEffect.scene, gameState.irisEffect.camera);
    }
  } else if (gameState.isInHouse) {
    if (gameState.houseInterior) {
      if (composer && renderPass) {
        renderPass.scene = gameState.houseInterior;
        renderPass.camera = camera;
        composer.render();
      } else {
        renderer.render(gameState.houseInterior, camera);
      }
    }
  } else {
    if (composer && renderPass) {
      renderPass.scene = scene;
      renderPass.camera = camera;
      composer.render();
    } else {
      renderer.render(scene, camera);
    }
  }

  // Update FPS counter
  if (stats) {
    stats.update();
  }
  
  // Update wind effect if it exists
  if (window.updateWindEffect) {
    window.updateWindEffect(actualDeltaTime);
  }
}

// Helper function for shadow animation, ensures value wraps around 0-1 range
function fract(value) {
    return value - Math.floor(value);
}

// Clock already initialized above

// Initialize the scene and start loading game assets
function initScene() {
  // Create texture loaders that use our game assets manager
  const textureLoader = new THREE.TextureLoader(gameAssetsManager);
  const gltfLoader = new GLTFLoader(gameAssetsManager);
  
  // Initialize 3D models and textures with the game assets manager
  initializeFishModels(gltfLoader);
  
  // Create sky clouds using the game assets manager
  createSkyClouds();
  
  // Load splat map and terrain textures
  loadSplatMap();
  loadTerrainTextures();
  
  // Create a variable to reference the ground once it's created
  let groundReference = null;
  
  // Create a callback function to be called once the ground is created
  const onGroundCreated = (ground) => {
    groundReference = ground;
    // Now we can safely apply the texture to the ground
    loadAndApplyCloudShadow(ground);
    
    // Add the stylized rock border around the circular ground
    createStylizedRockBorder();
  };
  
  // Make the onGroundCreated function available globally so other parts of the code can use it
  window.onGroundCreated = onGroundCreated;
  
  // Function to create a stylized rock border around the circular ground
  function createStylizedRockBorder() {
    console.log('Creating stylized rock border...');
    
    // First remove any existing rock borders to avoid duplicates
    scene.children.forEach(child => {
      if (child.userData && child.userData.isRockBorder) {
        scene.remove(child);
      }
    });
    
    // Border parameters
    const borderRadius = groundRadius + 0.5; // Just slightly larger than ground
    const borderWidth = 6; // Wider, more visible border
    const borderSegments = 72; // Higher for more detail to work with
    const variationAmount = 0.2; // More distortion for hand-drawn look
    
    // Create a ring geometry for our border
    const borderGeometry = new THREE.RingGeometry(
      borderRadius, // Inner radius
      borderRadius + borderWidth, // Outer radius
      borderSegments, // Segment count
      1, // Height segments
      0, // Start angle
      Math.PI * 2 // End angle (full circle)
    );
    
    // Apply hand-drawn irregularity to the vertices
    const vertices = borderGeometry.attributes.position;
    for (let i = 0; i < vertices.count; i++) {
      // Get vertex
      const x = vertices.getX(i);
      const y = vertices.getY(i);
      const z = vertices.getZ(i);
      
      // Don't move vertices at z != 0 (they're part of the geometry definition)
      if (z !== 0) continue;
      
      // Calculate distance from center (0,0)
      const distance = Math.sqrt(x*x + y*y);
      
      // Only distort if it's on the border
      if (distance > 0) {
        // Normalize the position to get the angle
        const angle = Math.atan2(y, x);
        
        // Determine which ring (inner or outer)
        const isOuterRing = distance > borderRadius + (borderWidth / 2);
        
        // Add some noise that varies by angle (hand-drawn effect)
        // Each point gets a unique distortion based on its angle and whether it's inner or outer
        const seed = (Math.sin(angle * 4) + Math.cos(angle * 7)) * 0.5;
        const extraNoise = Math.sin(angle * 13) * Math.cos(angle * 5) * 0.3;
        
        // Different distortion for inner and outer rings
        const distortAmount = isOuterRing ? 
          borderWidth * variationAmount * (0.8 + 0.4 * seed + extraNoise) :
          borderWidth * variationAmount * (0.7 + 0.3 * seed - extraNoise);
        
        // Distort more/less based on various factors for uneven look
        const targetDistance = isOuterRing ? 
          borderRadius + borderWidth + distortAmount :
          borderRadius - distortAmount * 0.8;
        
        // Set the new vertex position
        vertices.setX(i, Math.cos(angle) * targetDistance);
        vertices.setY(i, Math.sin(angle) * targetDistance);
      }
    }
    
    // Let Three.js know we've modified the vertices
    vertices.needsUpdate = true;
    
    // Create rock materials - using a darker, more visible material
    // First try to use the existing rock texture
    let borderMaterial;
    if (rockTexture) {
      borderMaterial = new THREE.MeshStandardMaterial({
        map: rockTexture,
        roughness: 0.9,
        metalness: 0.1,
        color: 0x505050, // Darker gray for better visibility
        side: THREE.DoubleSide
      });
    } else {
      // Fallback if texture isn't loaded yet
      borderMaterial = new THREE.MeshStandardMaterial({
        color: 0x505050, // Dark gray
        roughness: 0.9,
        metalness: 0.1,
        side: THREE.DoubleSide
      });
      
      // Try to load the texture directly
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load('/textures/albedo-rock.png', function(texture) {
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(8, 8); // Tighter tiling for more detail
        borderMaterial.map = texture;
        borderMaterial.needsUpdate = true;
        console.log('Loaded rock texture for border');
      }, undefined, function(err) {
        console.warn('Could not load rock texture for border:', err);
      });
    }
    
    // Create the border mesh
    const borderMesh = new THREE.Mesh(borderGeometry, borderMaterial);
    borderMesh.rotation.x = -Math.PI / 2; // Same rotation as the ground
    borderMesh.position.y = 0.01; // Slightly ABOVE the ground to be visible
    borderMesh.receiveShadow = true;
    borderMesh.userData.isRockBorder = true;
    scene.add(borderMesh);
    console.log('Rock border added to scene');

    return borderMesh;
  }
}

// Animation loop will start after the welcome screen
// Only auto-start if we're reloading the page and assets are already cached
if (document.readyState === 'complete') {
  window.setTimeout(() => {
    // If loading completes very quickly (e.g., cached assets), auto-start after 1 second
    if (loadingComplete && !gameStarted) {
      startGame();
    }
  }, 1000);
}

// Initialize inventory with all possible fish as undiscovered
function initializeInventory() {
  // Make sure we have all fish types in the inventory
  FISH_TYPES.forEach(fishType => { // Use fishType to avoid conflict
    // Use the unique fish ID for inventory keys
    if (!gameState.inventory[fishType.id]) {
      gameState.inventory[fishType.id] = {
        fish: fishType, // Store the whole fish object from FISH_TYPES
        count: 0,
        discovered: false
      };
    }
  });
  
  // Log inventory for debugging
  console.log("Initialized inventory:", gameState.inventory);
}

// Update the inventory UI
function updateInventoryUI() {
  const inventoryContent = document.getElementById('inventory-content');
  inventoryContent.innerHTML = '';

  // Sort fish by rarity and then alphabetically
  const rarityOrder = {
    'legendary': 0,
    'rare': 1,
    'uncommon': 2,
    'common': 3
  };

  const sortedFish = Object.entries(gameState.inventory).sort((a, b) => {
    // First sort by rarity
    const rarityDiff = rarityOrder[a[1].fish.rarity] - rarityOrder[b[1].fish.rarity];
    if (rarityDiff !== 0) return rarityDiff;
    
    // Then sort alphabetically
    return a[1].fish.name.localeCompare(b[1].fish.name);
  });

  sortedFish.forEach(([fishId, fishData], index) => {
    const discovered = fishData.discovered;
    const currentFish = fishData.fish; // The actual fish object from FISH_TYPES
    
    const fishCard = document.createElement('div');
    fishCard.style.position = 'relative'; // For positioning index and count
    fishCard.style.display = 'flex';
    fishCard.style.flexDirection = 'column';
    fishCard.style.alignItems = 'center';
    fishCard.style.width = '130px'; // Card width
    fishCard.style.minHeight = '180px'; // Card min height
    fishCard.style.padding = '10px';
    fishCard.style.margin = '8px';
    fishCard.style.border = '1px solid #ccc';
    fishCard.style.borderRadius = '8px';
    fishCard.style.boxShadow = '2px 2px 5px rgba(0,0,0,0.1)';
    fishCard.style.backgroundColor = '#f9f9f9';

    if (discovered) {
      fishCard.className = `fish-card ${currentFish.rarity.toLowerCase()}`;
    } else {
      fishCard.className = 'fish-card undiscovered';
      fishCard.style.backgroundColor = '#e0e0e0';
    }
    fishCard.dataset.fishId = fishId;
    
    // Add index number (top-left corner)
    const indexNumber = document.createElement('div');
    indexNumber.className = 'fish-index';
    indexNumber.textContent = `#${(index + 1).toString().padStart(3, '0')}`;
    indexNumber.style.position = 'absolute';
    indexNumber.style.top = '5px';
    indexNumber.style.left = '5px';
    indexNumber.style.fontSize = '0.7em';
    indexNumber.style.color = '#555';
    fishCard.appendChild(indexNumber);
    
    const fishImage = document.createElement('div');
    fishImage.className = 'fish-image';
    fishImage.style.marginTop = '15px'; // Space for index number
    fishImage.style.marginBottom = '10px'; // Space below image

    if (discovered) {
      fishImage.style.backgroundImage = `url(${currentFish.image_url})`;
      fishImage.style.backgroundSize = '300% 300%'; 
      const xPos = currentFish.sprite_col * 50;
      const yPos = currentFish.sprite_row * 50;
      fishImage.style.backgroundPosition = `${xPos}% ${yPos}%`;
      fishImage.style.width = '80px'; 
      fishImage.style.height = '80px';
      // fishImage.style.imageRendering = 'pixelated'; // Removed for smoother scaling
      fishImage.style.border = '1px solid #ddd';
      fishImage.style.borderRadius = '4px';
    } else {
      fishImage.textContent = '?';
      fishImage.style.width = '80px';
      fishImage.style.height = '80px';
      fishImage.style.display = 'flex';
      fishImage.style.alignItems = 'center';
      fishImage.style.justifyContent = 'center';
      fishImage.style.border = '2px dashed #aaa';
      fishImage.style.borderRadius = '4px';
      fishImage.style.fontSize = '2em';
      fishImage.style.color = '#888';
    }
    fishCard.appendChild(fishImage);
    
    const fishName = document.createElement('div');
    fishName.className = 'fish-name';
    fishName.textContent = discovered ? currentFish.name : '???';
    fishName.style.fontWeight = 'bold';
    fishName.style.fontSize = '0.9em';
    fishName.style.textAlign = 'center';
    fishName.style.marginTop = '5px';
    fishCard.appendChild(fishName);
    
    const fishRarity = document.createElement('div');
    fishRarity.className = 'fish-rarity';
    fishRarity.textContent = discovered ? currentFish.rarity : '???';
    fishRarity.style.color = discovered ? getRarityColor(currentFish.rarity) : '#666';
    fishRarity.style.fontSize = '0.8em';
    fishRarity.style.fontStyle = 'italic';
    fishRarity.style.textAlign = 'center';
    fishRarity.style.marginBottom = '10px';
    fishCard.appendChild(fishRarity);
    
    const fishCount = document.createElement('div');
    fishCount.className = 'fish-count';
    fishCount.textContent = discovered ? `×${fishData.count}` : '';
    fishCount.style.position = 'absolute';
    fishCount.style.bottom = '5px';
    fishCount.style.right = '8px';
    fishCount.style.fontSize = '0.9em';
    fishCount.style.fontWeight = 'bold';
    fishCard.appendChild(fishCount);
    
    if (discovered) {
      fishCard.title = `${currentFish.name} (${currentFish.rarity})\nCaught: ${fishData.count}`;
    } else {
      fishCard.title = 'Not yet discovered';
    }
    
    fishCard.addEventListener('click', () => {
      if (discovered) {
        showFishDetail(fishId);
      }
    });
    
    fishCard.addEventListener('click', () => {
      if (discovered) {
        showFishDetail(fishId);
      }
    });
    
    inventoryContent.appendChild(fishCard);
  });
  
  updateInventoryStats();
}

function showFishDetail(fishId) {
  const fishEntry = gameState.inventory[fishId];
  const fish = fishEntry.fish;
  const detailView = document.getElementById('fish-detail');
  const detailTitle = document.getElementById('detail-title');
  const detailImage = document.getElementById('detail-image');
  const detailIndex = document.getElementById('detail-index');
  
  // Find the index of this fish in the sorted list
  const rarityOrder = {
    'legendary': 0,
    'rare': 1,
    'uncommon': 2,
    'common': 3
  };

  const sortedFish = Object.entries(gameState.inventory).sort((a, b) => {
    // First sort by rarity
    const rarityDiff = rarityOrder[a[1].fish.rarity] - rarityOrder[b[1].fish.rarity];
    if (rarityDiff !== 0) return rarityDiff;
    
    // Then sort alphabetically
    return a[1].fish.name.localeCompare(b[1].fish.name);
  });
  
  const index = sortedFish.findIndex(([id]) => id === fishId) + 1;
  
  // Set fish details
  detailTitle.textContent = fish.name;
  detailIndex.textContent = `#${index.toString().padStart(3, '0')}`;
  detailImage.innerHTML = ''; // Clear previous image or img tag
  // --- Sprite Sheet Display for Detail View ---
  detailImage.style.backgroundImage = `url(${fish.image_url})`; 
  detailImage.style.backgroundSize = '300% 300%'; 
  const xPosDetail = fish.sprite_col * 50;
  const yPosDetail = fish.sprite_row * 50;
  detailImage.style.backgroundPosition = `${xPosDetail}% ${yPosDetail}%`;
  detailImage.style.width = '128px'; 
  detailImage.style.height = '128px';
  detailImage.style.margin = '0 auto 20px auto'; 
  // detailImage.style.imageRendering = 'pixelated'; // Removed for smoother scaling
  detailImage.style.border = '2px solid #555'; 

  // Set basic information
  document.getElementById('detail-name').textContent = fish.name;
  document.getElementById('detail-rarity').textContent = fish.rarity;
  document.getElementById('detail-rarity').style.color = getRarityColor(fish.rarity);
  document.getElementById('detail-caught').textContent = fishEntry.count;
  document.getElementById('detail-points').textContent = fish.points || 'N/A';
  
  // Set description (generate a fun description if none exists)
  const descriptions = {
    'common': [
      "A very common fish found in many waters.",
      "Not particularly rare, but still a nice catch!",
      "Fishermen catch these regularly in these waters."
    ],
    'uncommon': [
      "A somewhat uncommon fish that requires some patience to catch.",
      "Not everyone manages to catch this one!",
      "A moderately challenging catch for intermediate anglers."
    ],
    'rare': [
      "A rare specimen that few anglers have seen!",
      "This elusive fish is a prized catch among collectors.",
      "Only the most patient fishermen catch this beauty."
    ],
    'legendary': [
      "An extremely rare legendary fish! You're very lucky to have caught this!",
      "Stories are told about this mythical creature of the deep.",
      "Few believe this fish even exists. A truly magnificent catch!"
    ]
  };
  
  // Either use the fish's description or generate a random one based on rarity
  const description = fish.description || 
    descriptions[fish.rarity][Math.floor(Math.random() * descriptions[fish.rarity].length)];
  document.getElementById('detail-description').textContent = description;
  
  // Set habitat information
  document.getElementById('detail-location').textContent = fish.location || 'Unknown Waters';
  document.getElementById('detail-depth').textContent = fish.depth || 'Various Depths';
  
  // Set first caught date (or generate one)
  const firstCaught = fishEntry.firstCaught || 
    new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString();
  document.getElementById('detail-first-caught').textContent = firstCaught;
  
  // Show the detail view with a slight delay for animation
  setTimeout(() => {
    detailView.style.display = 'flex';
  }, 50);
  
  // Add event listener to close button
  const closeButton = document.getElementById('detail-close');
  closeButton.onclick = () => {
    detailView.style.display = 'none';
  };
  
  // Close when clicking outside the content
  detailView.onclick = (event) => {
    if (event.target === detailView) {
      detailView.style.display = 'none';
    }
  };
  
  // Add keyboard event listener to close with Escape key
  const escapeHandler = (e) => {
    if (e.key === 'Escape') {
      detailView.style.display = 'none';
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);
}

function getRarityColor(rarity) {
  switch(rarity.toLowerCase()) {
    case 'legendary': return '#ffd700'; // Gold
    case 'rare': return '#ff5c8d';      // Pink
    case 'uncommon': return '#4fc3f7';  // Light blue
    case 'common': return '#a5d6a7';    // Light green
    default: return 'white';
  }
}

// Update inventory stats
function updateInventoryStats() {
  // Calculate stats
  const totalSpecies = FISH_TYPES.length;
  const discoveredSpecies = Object.values(gameState.inventory).filter(item => item.discovered).length;
  const totalCaught = gameState.fishCaught;
  const totalScore = gameState.score;
  
  // Update UI
  document.getElementById('total-species').textContent = `${discoveredSpecies}/${totalSpecies}`;
  document.getElementById('total-caught').textContent = totalCaught;
  document.getElementById('total-score').textContent = totalScore;
}

// Toggle inventory display
function toggleInventory() {
  gameState.showInventory = !gameState.showInventory;
  const inventoryPanel = document.getElementById('inventory-panel');
  
  if (gameState.showInventory) {
    // Always update the UI when opening
    updateInventoryUI();
    inventoryPanel.style.display = 'flex';
  } else {
    inventoryPanel.style.display = 'none';
  }
}

// Setup inventory event listeners
function setupInventoryListeners() {
  const inventoryButton = document.getElementById('inventory-button');
  const closeButton = document.getElementById('inventory-close');
  
  inventoryButton.addEventListener('click', () => {
    // Debug: log inventory state when opening
    console.log("Current inventory state:", gameState.inventory);
    toggleInventory();
  });
  
  closeButton.addEventListener('click', toggleInventory);
  
  // Also close inventory with Escape key
  window.addEventListener('keydown', (event) => {
    if (event.code === 'Escape' && gameState.showInventory) {
      toggleInventory();
    }
  });
}

// Import the WindEffect class
import { WindEffect } from './WindEffect.js';

// Initialize the Wind Waker style wind effect
function initWindEffect() {
  // Create the wind effect visible from top-down camera
  const windEffect = new WindEffect(scene, {
    count: 3,             // More wind lines for visibility
    height: 5.0,          // Lower height so it's visible in camera view
    minRadius: 10,        // Minimum radius
    maxRadius: groundRadius * 0.7, // Maximum radius
    lineWidth: 0.6,       // Thinner lines
    lineColor: 0xffffff,  // White color like in Wind Waker
    opacity: 0.35,        // Subtle opacity
    rotationSpeed: 0.002, // Slow rotation
    animationSpeed: 0.015,// Animation speed
    pattern: 'linear'     // Linear pattern
  });
  
  // Set initial visibility
  const windEnabled = localStorage.getItem('windEffectEnabled') !== 'false';
  windEffect.setVisible(windEnabled);
  
  // Function to toggle wind effect
  window.toggleWindEffect = function() {
    const isVisible = !windEffect.group.visible;
    windEffect.setVisible(isVisible);
    localStorage.setItem('windEffectEnabled', isVisible);
    
    // Show toggle notification
    showNotification(`Wind effect ${isVisible ? 'enabled' : 'disabled'}`, 1500);
  };
  
  // Add keyboard shortcut to toggle wind (W key)
  window.addEventListener('keydown', (event) => {
    if (event.key === 'w' || event.key === 'W') {
      window.toggleWindEffect();
    }
  });
  
  // Add the wind effect toggle to game controls info
  const welcomeMessage = document.getElementById('welcome-message');
  if (welcomeMessage) {
    const windToggleInfo = document.createElement('p');
    windToggleInfo.innerHTML = 'Press <strong>W</strong> to toggle wind effect.';
    welcomeMessage.appendChild(windToggleInfo);
  }
  
  // Update function for animation loop
  window.updateWindEffect = (deltaTime) => {
    if (windEffect && windEffect.group.visible) {
      windEffect.update(deltaTime);
    }
  };
}

// Initialize the game
function initGame() {
  initializeInventory();
  setupInventoryListeners();
  initWindEffect();

  // Initialize post-processing for Wind Waker style visuals
  initPostProcessing();
  console.log('Post-processing initialized:', { composer, renderPass, bloomPass, colorEnhancePass });

  // Initialize core game state with fish types for achievements/progression tracking
  coreGameState.initializeInventory(FISH_TYPES);
  coreGameState.mergeSavedInventory();

  // Set up system event listeners
  setupSystemListeners();
}

// Set up event listeners for achievements and progression notifications
function setupSystemListeners() {
  // Achievement unlocked notification
  eventBus.on(GameEvents.ACHIEVEMENT_UNLOCKED, ({ achievement }) => {
    showNotification(
      `Achievement: ${achievement.name}! ${achievement.icon}`,
      3000,
      'legendary'
    );
  });

  // Area unlocked notification
  eventBus.on(GameEvents.AREA_UNLOCKED, ({ pondIndex, pondName }) => {
    showNotification(
      `New Area Unlocked: ${pondName}!`,
      3000,
      'rare'
    );

    // Update pond visuals - remove locked overlay/icon and show shimmer if applicable
    const pond = fishingHoles[pondIndex];
    if (pond) {
      if (pond.lockedOverlay) {
        pond.lockedOverlay.visible = false;
      }
      if (pond.lockSprite) {
        pond.lockSprite.visible = false;
      }
      if (pond.shimmerMesh) {
        pond.shimmerMesh.visible = true;
      }
    }
  });

  // Rod upgraded notification
  eventBus.on(GameEvents.ROD_UPGRADED, ({ name, effect }) => {
    showNotification(
      `Rod Upgraded: ${name}! (${effect})`,
      3000,
      'rare'
    );
  });
}

// Call init after the scene is set up
initGame();

// Background Music and Audio Controls
const backgroundMusic = document.getElementById('background-music');
const muteButton = document.getElementById('mute-button');
const volumeUpButton = document.getElementById('volume-up');
const volumeDownButton = document.getElementById('volume-down');

if (backgroundMusic && muteButton) {
  // Set initial volume (0.5 = 50%)
  backgroundMusic.volume = 0.5;
  
  // Autoplay can be blocked by browsers, attempt to play once user interacts
  const playMusic = () => {
    backgroundMusic.play().catch(error => console.log("Autoplay prevented: ", error));
    // Remove the event listener after the first interaction
    document.removeEventListener('click', playMusic);
    document.removeEventListener('keydown', playMusic);
  };

  document.addEventListener('click', playMusic);
  document.addEventListener('keydown', playMusic);

  // Mute button functionality
  muteButton.addEventListener('click', () => {
    backgroundMusic.muted = !backgroundMusic.muted;
    muteButton.textContent = backgroundMusic.muted ? 'Unmute' : 'Mute';
  });
  
  // Function to increase volume
  const increaseVolume = () => {
    // Increase volume by 10% but don't exceed 1.0 (100%)
    backgroundMusic.volume = Math.min(backgroundMusic.volume + 0.1, 1.0);
    console.log(`Volume increased to: ${Math.round(backgroundMusic.volume * 100)}%`);
  };

  // Function to decrease volume
  const decreaseVolume = () => {
    // Decrease volume by 10% but don't go below 0.0 (0%)
    backgroundMusic.volume = Math.max(backgroundMusic.volume - 0.1, 0.0);
    console.log(`Volume decreased to: ${Math.round(backgroundMusic.volume * 100)}%`);
  };
  
  // Volume control button functionality
  if (volumeUpButton) {
    volumeUpButton.addEventListener('click', increaseVolume);
  }
  
  if (volumeDownButton) {
    volumeDownButton.addEventListener('click', decreaseVolume);
  }
  
  // Keyboard controls for volume
  document.addEventListener('keydown', (event) => {
    // Check if we're not in a text input field
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) {
      if (event.key === '+' || event.key === '=') { // '=' is on the same key as '+' without shift
        increaseVolume();
      } else if (event.key === '-' || event.key === '_') { // '_' is on the same key as '-' with shift
        decreaseVolume();
      } else if (event.key === 'm' || event.key === 'M') { // Add 'M' key for mute toggle
        backgroundMusic.muted = !backgroundMusic.muted;
        muteButton.textContent = backgroundMusic.muted ? 'Unmute' : 'Mute';
      }
    }
  });
}

// ============================================
// MULTIPLAYER SYSTEM
// ============================================

const multiplayerState = {
  ws: null,
  playerId: null,
  playerName: 'Player',
  remotePlayers: new Map(), // Map<playerId, { model, nameTag, mixer, walkAction }>
  connected: false,
  lastSentPosition: { x: 0, y: 0, z: 0, rotationY: 0 },
  updateInterval: null
};

// Create a name tag sprite for a player
function createNameTag(name) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');

  // Set font FIRST before measuring
  ctx.font = 'bold 24px Arial';

  // Draw background pill
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  const textWidth = ctx.measureText(name).width;
  const pillWidth = Math.min(textWidth + 30, 240);
  const pillX = (256 - pillWidth) / 2;
  ctx.beginPath();
  ctx.roundRect(pillX, 12, pillWidth, 40, 20);
  ctx.fill();

  // Draw text
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffffff';
  ctx.fillText(name, 128, 32);

  const texture = new THREE.CanvasTexture(canvas);
  const spriteMaterial = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    sizeAttenuation: true
  });
  const sprite = new THREE.Sprite(spriteMaterial);
  sprite.scale.set(2.5, 0.625, 1);
  sprite.renderOrder = 999;

  return sprite;
}

// Update name tag by replacing it entirely
function updateNameTag(oldNameTag, name, playerInfo) {
  console.log(`[Multiplayer] updateNameTag called with name: ${name}`);

  // Remove old name tag from parent
  const parent = oldNameTag.parent;
  if (parent) {
    parent.remove(oldNameTag);
    oldNameTag.material.map.dispose();
    oldNameTag.material.dispose();
  }

  // Create new name tag
  const newNameTag = createNameTag(name);
  newNameTag.position.set(0, 2.5, 0);

  if (parent) {
    parent.add(newNameTag);
  }

  // Update reference in playerInfo
  if (playerInfo) {
    playerInfo.nameTag = newNameTag;
  }

  return newNameTag;
}

// Spawn a remote player character
function spawnRemotePlayer(playerData) {
  const { id, name, x, y, z, rotationY } = playerData;

  // Don't spawn ourselves
  if (id === multiplayerState.playerId) return;

  // Check if player already exists
  if (multiplayerState.remotePlayers.has(id)) return;

  console.log(`Spawning remote player ${id}: ${name}`);

  // Create a container for the remote player
  const container = new THREE.Group();
  container.position.set(x, y, z);
  container.rotation.y = rotationY || 0;

  // Create name tag
  const nameTag = createNameTag(name);
  nameTag.position.set(0, 2.5, 0);
  container.add(nameTag);

  // Store player data
  const playerInfo = {
    container,
    model: null,
    nameTag,
    mixer: null,
    walkAction: null,
    isMoving: false,
    targetPosition: new THREE.Vector3(x, y, z),
    targetRotation: rotationY || 0
  };

  multiplayerState.remotePlayers.set(id, playerInfo);
  scene.add(container);

  // Load the same character model for remote players
  const loader = new GLTFLoader();
  loader.load('/models/boy_character/scene.gltf', (gltf) => {
    const model = gltf.scene;
    model.scale.set(1, 1, 1);

    // Load textures
    const textureLoader = new THREE.TextureLoader();
    const bodyTexture = textureLoader.load('/models/boy_character/textures/Boy01_Body_MAT1_diffuse.jpeg');
    const mouthTexture = textureLoader.load('/models/boy_character/textures/Boy01_Mouth_MAT2_diffuse.png');
    bodyTexture.flipY = false;
    mouthTexture.flipY = false;

    model.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        const matName = (child.material.name || '').toLowerCase();

        if (matName.includes('body')) {
          child.material = new THREE.MeshToonMaterial({
            map: bodyTexture,
            side: THREE.DoubleSide
          });
        } else if (matName.includes('mouth') || matName.includes('eye') || matName.includes('brow')) {
          child.material = new THREE.MeshToonMaterial({
            map: mouthTexture,
            transparent: true,
            side: THREE.DoubleSide
          });
        }
      }
    });

    container.add(model);
    playerInfo.model = model;

    // Set up animation
    if (gltf.animations.length > 0) {
      const mixer = new THREE.AnimationMixer(model);
      playerInfo.mixer = mixer;

      gltf.animations.forEach(clip => {
        const clipName = clip.name.toLowerCase();
        if (clipName.includes('walk') || clipName.includes('run')) {
          playerInfo.walkAction = mixer.clipAction(clip);
          playerInfo.walkAction.setLoop(THREE.LoopRepeat);
        }
      });

      if (!playerInfo.walkAction && gltf.animations.length > 0) {
        playerInfo.walkAction = mixer.clipAction(gltf.animations[0]);
        playerInfo.walkAction.setLoop(THREE.LoopRepeat);
      }
    }
  });
}

// Remove a remote player
function removeRemotePlayer(playerId) {
  const playerInfo = multiplayerState.remotePlayers.get(playerId);
  if (playerInfo) {
    scene.remove(playerInfo.container);
    if (playerInfo.nameTag && playerInfo.nameTag.material.map) {
      playerInfo.nameTag.material.map.dispose();
    }
    multiplayerState.remotePlayers.delete(playerId);
    console.log(`Removed remote player ${playerId}`);
  }
}

// Update remote player position with interpolation
function updateRemotePlayer(playerId, data) {
  const playerInfo = multiplayerState.remotePlayers.get(playerId);
  if (!playerInfo) return;

  playerInfo.targetPosition.set(data.x, data.y, data.z);
  playerInfo.targetRotation = data.rotationY;
  playerInfo.isMoving = data.isMoving;

  // Handle walk animation
  if (playerInfo.walkAction) {
    if (data.isMoving && !playerInfo.walkAction.isRunning()) {
      playerInfo.walkAction.play();
    } else if (!data.isMoving && playerInfo.walkAction.isRunning()) {
      playerInfo.walkAction.stop();
      playerInfo.walkAction.reset();
    }
  }
}

// Update all remote players (called from animation loop)
function updateRemotePlayers(deltaTime) {
  multiplayerState.remotePlayers.forEach((playerInfo, id) => {
    // Interpolate position
    playerInfo.container.position.lerp(playerInfo.targetPosition, 0.15);

    // Interpolate rotation
    let rotDiff = playerInfo.targetRotation - playerInfo.container.rotation.y;
    while (rotDiff > Math.PI) rotDiff -= Math.PI * 2;
    while (rotDiff < -Math.PI) rotDiff += Math.PI * 2;
    playerInfo.container.rotation.y += rotDiff * 0.15;

    // Update animation mixer
    if (playerInfo.mixer) {
      playerInfo.mixer.update(deltaTime);
    }
  });
}

// Connect to multiplayer server
function connectMultiplayer() {
  // Auto-detect WebSocket URL based on environment
  const isProduction = window.location.hostname !== 'localhost';
  const wsUrl = isProduction
    ? `wss://${window.location.hostname}/ws`  // Production: use nginx proxy
    : 'ws://localhost:3001';                   // Development: direct connection

  try {
    multiplayerState.ws = new WebSocket(wsUrl);

    multiplayerState.ws.onopen = () => {
      console.log('Connected to multiplayer server');
      multiplayerState.connected = true;
      showNotification('Connected to multiplayer!', 2000);

      // Start sending position updates
      multiplayerState.updateInterval = setInterval(sendPositionUpdate, 50); // 20 updates/sec
    };

    multiplayerState.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        switch (msg.type) {
          case 'init':
            multiplayerState.playerId = msg.playerId;
            // Use player's chosen name or fallback to Player + ID
            multiplayerState.playerName = window.playerName || `Player${msg.playerId}`;
            console.log(`[Multiplayer] Assigned player ID: ${msg.playerId}, name: ${multiplayerState.playerName}`);
            console.log(`[Multiplayer] window.playerName = ${window.playerName}`);

            // Send our name to the server
            console.log(`[Multiplayer] Sending setName: ${multiplayerState.playerName}`);
            multiplayerState.ws.send(JSON.stringify({
              type: 'setName',
              name: multiplayerState.playerName
            }));

            // Spawn existing players
            console.log(`[Multiplayer] Spawning ${msg.players.length} existing players`);
            msg.players.forEach(p => spawnRemotePlayer(p));
            break;

          case 'playerJoin':
            spawnRemotePlayer(msg.player);
            showNotification(`${msg.player.name} joined!`, 2000);
            break;

          case 'playerLeave':
            const leaving = multiplayerState.remotePlayers.get(msg.playerId);
            if (leaving) {
              showNotification(`Player${msg.playerId} left`, 2000);
            }
            removeRemotePlayer(msg.playerId);
            break;

          case 'playerUpdate':
            updateRemotePlayer(msg.playerId, msg);
            break;

          case 'playerName':
            console.log(`[Multiplayer] Received playerName: ${msg.playerId} -> ${msg.name}`);
            const playerInfoForName = multiplayerState.remotePlayers.get(msg.playerId);
            if (playerInfoForName && playerInfoForName.nameTag) {
              console.log(`[Multiplayer] Updating name tag for player ${msg.playerId}`);
              updateNameTag(playerInfoForName.nameTag, msg.name, playerInfoForName);
            } else {
              console.log(`[Multiplayer] Player ${msg.playerId} not found or no nameTag`);
            }
            break;

          case 'chat':
            displayChatMessage(msg.playerName, msg.message, msg.playerId === multiplayerState.playerId);
            break;
        }
      } catch (e) {
        console.error('Error parsing multiplayer message:', e);
      }
    };

    multiplayerState.ws.onclose = () => {
      console.log('Disconnected from multiplayer server');
      multiplayerState.connected = false;

      if (multiplayerState.updateInterval) {
        clearInterval(multiplayerState.updateInterval);
        multiplayerState.updateInterval = null;
      }

      // Clean up remote players
      multiplayerState.remotePlayers.forEach((_, id) => removeRemotePlayer(id));

      showNotification('Disconnected from multiplayer', 2000);
    };

    multiplayerState.ws.onerror = (error) => {
      console.log('Multiplayer connection failed (server may not be running)');
    };

  } catch (e) {
    console.log('WebSocket not available or connection failed');
  }
}

// Send position update to server
function sendPositionUpdate() {
  if (!multiplayerState.connected || !multiplayerState.ws || !player) return;

  const pos = player.position;
  const rot = player.rotation.y;
  const moving = gameState.playerMoving;

  // Only send if position changed significantly
  const dx = pos.x - multiplayerState.lastSentPosition.x;
  const dz = pos.z - multiplayerState.lastSentPosition.z;
  const dr = rot - multiplayerState.lastSentPosition.rotationY;

  if (Math.abs(dx) > 0.01 || Math.abs(dz) > 0.01 || Math.abs(dr) > 0.01 ||
      moving !== multiplayerState.lastSentPosition.isMoving) {

    const msg = {
      type: 'update',
      x: pos.x,
      y: pos.y,
      z: pos.z,
      rotationY: rot,
      isMoving: moving
    };

    multiplayerState.ws.send(JSON.stringify(msg));

    multiplayerState.lastSentPosition = {
      x: pos.x,
      y: pos.y,
      z: pos.z,
      rotationY: rot,
      isMoving: moving
    };
  }
}

// Multiplayer will connect when startGame() is called
// Don't auto-connect here - wait for user to enter name and click Play

// Add multiplayer update to animation loop
const originalAnimate = animate;
window._multiplayerUpdateHooked = false;

// Hook into animation loop for remote player updates
const animateWithMultiplayer = () => {
  if (multiplayerState.connected) {
    updateRemotePlayers(window.actualDeltaTime || 0.016);
  }
};

// Patch the animate function to include multiplayer updates
const patchedAnimateCheck = setInterval(() => {
  if (typeof window.actualDeltaTime !== 'undefined' && !window._multiplayerUpdateHooked) {
    window._multiplayerUpdateHooked = true;
    clearInterval(patchedAnimateCheck);

    // Add multiplayer update call after render in animation loop
    const originalRaf = window.requestAnimationFrame;
    let frameCount = 0;
    window.requestAnimationFrame = function(callback) {
      return originalRaf.call(window, (timestamp) => {
        callback(timestamp);
        // Update remote players every frame
        if (multiplayerState.connected) {
          updateRemotePlayers(window.actualDeltaTime || 0.016);
        }
      });
    };
  }
}, 100);

// ============================================
// CHAT SYSTEM
// ============================================

// Create chat UI
function createChatUI() {
  // Check if already created
  if (document.getElementById('chat-container')) return;

  // Chat container
  const chatContainer = document.createElement('div');
  chatContainer.id = 'chat-container';
  chatContainer.style.cssText = `
    position: fixed;
    bottom: 80px;
    left: 20px;
    width: 280px;
    z-index: 9000;
    font-family: Arial, sans-serif;
    pointer-events: auto;
  `;

  // Chat messages
  const chatMessages = document.createElement('div');
  chatMessages.id = 'chat-messages';
  chatMessages.style.cssText = `
    max-height: 120px;
    overflow-y: auto;
    margin-bottom: 8px;
    padding: 8px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 8px;
    display: block;
    min-height: 30px;
  `;

  // Chat input container
  const chatInputContainer = document.createElement('div');
  chatInputContainer.style.cssText = `
    display: flex;
    gap: 8px;
  `;

  // Chat input
  const chatInput = document.createElement('input');
  chatInput.id = 'chat-input';
  chatInput.type = 'text';
  chatInput.placeholder = 'Press Enter to chat...';
  chatInput.maxLength = 200;
  chatInput.style.cssText = `
    flex: 1;
    padding: 8px 12px;
    border: none;
    border-radius: 20px;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    font-size: 14px;
    outline: none;
  `;

  // Send button
  const sendButton = document.createElement('button');
  sendButton.textContent = '➤';
  sendButton.style.cssText = `
    padding: 8px 14px;
    border: none;
    border-radius: 20px;
    background: #4CAF50;
    color: white;
    cursor: pointer;
    font-size: 14px;
  `;

  chatInputContainer.appendChild(chatInput);
  chatInputContainer.appendChild(sendButton);
  chatContainer.appendChild(chatMessages);
  chatContainer.appendChild(chatInputContainer);
  document.body.appendChild(chatContainer);

  // Event listeners
  chatInput.addEventListener('keydown', (e) => {
    e.stopPropagation(); // Prevent game controls
    if (e.key === 'Enter') {
      e.preventDefault();
      if (chatInput.value.trim()) {
        sendChatMessage(chatInput.value.trim());
        chatInput.value = '';
      }
    }
    if (e.key === 'Escape') {
      chatInput.blur();
    }
  });

  sendButton.addEventListener('click', () => {
    if (chatInput.value.trim()) {
      sendChatMessage(chatInput.value.trim());
      chatInput.value = '';
    }
  });

  // Global Enter key to focus chat
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && document.activeElement !== chatInput) {
      e.preventDefault();
      chatInput.focus();
    }
  });
}

// Send chat message
function sendChatMessage(message) {
  if (!multiplayerState.connected || !multiplayerState.ws) {
    showNotification('Not connected to multiplayer', 2000);
    return;
  }

  multiplayerState.ws.send(JSON.stringify({
    type: 'chat',
    message: message
  }));
}

// Display chat message
function displayChatMessage(playerName, message, isOwnMessage) {
  console.log(`[Chat] ${playerName}: ${message}`);

  const chatMessages = document.getElementById('chat-messages');
  if (!chatMessages) {
    console.warn('Chat messages container not found');
    return;
  }

  const msgElement = document.createElement('div');
  msgElement.style.cssText = `
    margin-bottom: 6px;
    padding: 6px 10px;
    border-radius: 6px;
    background: ${isOwnMessage ? 'rgba(76, 175, 80, 0.4)' : 'rgba(255, 255, 255, 0.15)'};
    word-wrap: break-word;
    font-size: 13px;
  `;

  const nameSpan = document.createElement('span');
  nameSpan.style.cssText = `
    font-weight: bold;
    color: ${isOwnMessage ? '#8BC34A' : '#64B5F6'};
  `;
  nameSpan.textContent = playerName + ': ';

  const msgSpan = document.createElement('span');
  msgSpan.style.color = 'white';
  msgSpan.textContent = message;

  msgElement.appendChild(nameSpan);
  msgElement.appendChild(msgSpan);
  chatMessages.appendChild(msgElement);

  // Scroll to bottom
  chatMessages.scrollTop = chatMessages.scrollHeight;

  // Keep max 50 messages
  while (chatMessages.children.length > 50) {
    chatMessages.removeChild(chatMessages.firstChild);
  }
}

// Initialize chat UI when game starts
setTimeout(createChatUI, 500);